/* AstaLab 2.0 — /auction/:id — vista minima di un'asta aperta (fondamenta,
   obiettivo 6). L'editor completo (listone/tier list/asta live/...) della
   1.3 non viene ancora integrato in questo round (vedi ARCHITECTURE_2.md,
   roadmap): questa pagina mostra lo stato REALE già persistito (revision,
   ultimo aggiornamento, contenuto se importato da un backup legacy) senza
   inventare funzionalità non ancora presenti.

   Revisione r11 (audit, bug operativo "nuova asta = dead end"): PRIMA di questa correzione, se
   un'asta non aveva ancora uno stato salvato (revision 0, il caso NORMALE per ogni asta appena
   creata dalla UI — vedi ROOT_CAUSE.md), questa pagina mostrava solo un messaggio statico, senza
   alcuna azione per uscirne: nessun percorso portava a configurazione/partecipanti/budget/rose.
   Ora, quando l'utente è il proprietario, questo stesso ramo mostra un form di "prima
   configurazione" che chiama createInitialAuctionState() (frontend/src/engine/
   createInitialAuctionState.js, NUOVO in questo round) e salva il risultato con
   AuctionRepository.saveAuction() — la STESSA identica funzione di persistenza già usata da
   sempre, mai una scrittura diretta all'adapter, mai un secondo percorso di salvataggio. */
import { escapeHtml } from "../components/escapeHtml.js";
import { createInitialAuctionState, validateInitialAuctionSetup } from "../../engine/createInitialAuctionState.js";
import { formatJoinCodeForDisplay, canonicalizeJoinCode } from "../../engine/joinCode.js";
import { start as startLivePolling } from "../../live/livePollingController.js";
import { logClientError } from "../../utils/clientLogger.js";
// A7-P1-7: riuso diretto della funzione pura già esportata da CatalogPage.js (round A7-P1-4) — mai
// una seconda implementazione divergente dello stesso filtro testuale. Nessun import inverso
// (CatalogPage.js non importa mai nulla da questo file, verificato staticamente): nessun ciclo.
import { filterCatalogPlayers } from "./CatalogPage.js";

function fmtDate(iso){
  if(!iso) return "—";
  try{ return new Date(iso).toLocaleString("it-IT"); }catch(e){ return iso; }
}

// Round alpha.5-r4-r1: id univoco per la sola idempotenza lato client (mai un'identità di sicurezza) —
// stabile per l'intera vita del comando (mai rigenerato a ogni retry, quella responsabilità resta del
// chiamante: qui ne generiamo uno nuovo a ogni submit, un retry manuale dell'utente crea quindi
// deliberatamente un comando NUOVO, non un replay — coerente con "il commandId lo decide chi chiama").
// CORREZIONE round alpha.5-r4-r2 (BLOCKER 2, audit indipendente): p_command_id è tipizzato `uuid`
// lato SQL — un valore come "cmd_..." non è un UUID valido e una chiamata reale (SupabaseBackendAdapter)
// verrebbe rifiutata dal database con un errore di tipo, mai un rifiuto applicativo leggibile.
// commandId NON è un'identità di sicurezza (non richiede un CSPRNG), ma la sua FORMA deve sempre
// rispettare il tipo SQL reale — sia sul percorso primario (crypto.randomUUID(), v4 reale) sia sul
// fallback (v4 sintatticamente valido via Math.random(), mai un prefisso "cmd_...").
function genCommandId(){
  try{ if(typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); }catch(e){}
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Un solo countdown attivo per volta in tutta la pagina — ripulito ESPLICITAMENTE a ogni nuovo render
// (mai lasciato a girare su un DOM ormai sostituito, mai due interval sovrapposti dopo una sequenza
// rapida di azioni/re-render).
let _nominationCountdownIntervalId = null;
export function clearNominationCountdown(){
  if(_nominationCountdownIntervalId !== null){
    clearInterval(_nominationCountdownIntervalId);
    _nominationCountdownIntervalId = null;
  }
}

// Round alpha.6 P0.2-A (CORREZIONE O+P, ALPHA6_P0_2_LIVE_POLLING_DESIGN.md §8): target MUTABILE del
// countdown, mai una `const` catturata in closure come nella versione pre-P0.2 — permette a un
// `deadlineAt` più recente arrivato dal poll (§2) di aggiornare il countdown già attivo, ANCHE dopo
// che la scadenza locale precedente aveva già disabilitato/fermato tutto (recovery post-scadenza,
// CORREZIONE P). Unica primitive condivisa fra il cold-render (wireNominationSection) e la patch non
// strutturale del poll — mai due implementazioni divergenti.
let _nominationCountdownDeadlineMs = null;

export function renderNominationCountdownTick(countdownEl, finalizeBtn){
  const remainingMs = _nominationCountdownDeadlineMs - Date.now();
  if(remainingMs <= 0){
    countdownEl.textContent = "Tempo scaduto";
    if(finalizeBtn) finalizeBtn.disabled = false;
    clearNominationCountdown();
    return;
  }
  countdownEl.textContent = "Tempo rimanente: " + Math.ceil(remainingMs/1000) + "s";
  if(finalizeBtn) finalizeBtn.disabled = true;
}

export function applyNominationCountdownDeadline(deadlineAtIso, countdownEl, finalizeBtn){
  _nominationCountdownDeadlineMs = deadlineAtIso ? new Date(deadlineAtIso).getTime() : null;
  if(_nominationCountdownDeadlineMs === null) return;
  renderNominationCountdownTick(countdownEl, finalizeBtn);
  if(_nominationCountdownDeadlineMs - Date.now() > 0 && _nominationCountdownIntervalId === null){
    _nominationCountdownIntervalId = setInterval(() => renderNominationCountdownTick(countdownEl, finalizeBtn), 1000);
  }
}

// Round alpha.6 P0.2-A (CORREZIONE A, §5-bis): l'UNICA istanza del controller di polling live
// attualmente viva, o null — variabile di modulo, stesso pattern già in uso per
// _nominationCountdownIntervalId sopra (mai per-istanza).
let _activeLiveController = null;

// Round A7-P1-6F (fix del "double-failure handoff" identificato in A7-P1-6V) — handoff dello
// stato-ledger nomine catturato da _activeLiveController.getActivityHandoff() SUBITO PRIMA di
// fermarlo (vedi disposeActiveAuctionDetailResources() sotto), o null se nessun controller è mai
// esistito per QUESTA stessa asta (vero cold-render iniziale). Scoped esplicitamente per auctionId
// (campo `auctionId` sulla entry stessa) — mai un handoff dell'asta A che sopravvive silenziosamente
// come baseline per un'asta B dopo una navigazione, stesso principio già in uso da
// ensureStatsLifecycleForAuction()/_statsCacheOwnerAuctionId sopra. Letto ESCLUSIVAMENTE dal blocco
// di fetch/fallback nomine e dalla costruzione di initialSnapshot più sotto — mai scritto altrove.
let _lastActivityHandoff = null;

// Round alpha.6 P0.2-A1 (CORREZIONE R): token monotono modulo-locale — ogni chiamata a
// disposeActiveAuctionDetailResources() lo incrementa, invalidando IMMEDIATAMENTE qualunque render
// generation eventualmente ancora in-flight (cold render, render strutturale avviato dal poll, render
// post-mutazione, o qualunque altra chiamata interna esistente a renderAuctionDetailPage). Un
// `renderAuctionDetailPage` in corso cattura il valore CORRENTE subito dopo aver chiamato dispose() —
// se quel valore non coincide più con `_renderGeneration` a un punto di ripresa asincrona successivo,
// quel render è STALE e deve fermarsi senza scrivere DOM/redirect/listener/countdown/controller.
let _renderGeneration = 0;

// Round alpha.7 (A7-P1-1, A7-P1-1D2 §8-9): stato statistiche giocatore, di proprietà di UN SOLO
// auctionId per volta — stesso spirito modulo-locale di _activeLiveController, ma con un ciclo di
// vita DIVERSO e più lungo: deve sopravvivere a un render STRUTTURALE della STESSA asta (mai
// resettato da disposeActiveAuctionDetailResources(), che gira anche lì — vedi
// ensureStatsLifecycleForAuction() e disposeAuctionDetailPageForNavigation() sotto), e resettarsi
// SOLO quando l'auctionId in ingresso cambia o quando si naviga davvero fuori da questa pagina.
let _statsCacheOwnerAuctionId = null;
let _statsSeasonResolved = false;   // distinto da _statsSeasonKey===null (un esito valido: "nessuna stagione")
let _statsSeasonKey = null;
let _statsCache = null;             // Map<"fantacalcioId:seasonKey", Promise<stats|null>>
let _statsPreviewToken = 0;         // token dedicato al pannello di anteprima candidato (§12) — MAI _renderGeneration
// Round alpha.7 (A7-P1-1A §4/§6): marker TERMINALE per-chiave di un fallimento già osservato in
// QUESTA stessa asta — mai una cache di successo (quella resta _statsCache, mai valori d'errore).
// Serve a distinguere "mai tentato" da "già tentato e fallito" per il SOLO percorso automatico
// (fetch della nomina attiva ad ogni render strutturale, §13): senza questo marker, un fallimento
// cancella la entry da _statsCache (§11, necessario per permettere un retry ESPLICITO dell'utente),
// ma il trigger automatico di §13 la richiamerebbe di nuovo a OGNI successivo render strutturale
// della stessa nomina attiva — un retry automatico "storm" mai voluto (A7-P1-1A §4). Un'interazione
// ESPLICITA (onchange dell'anteprima candidato) cancella sempre il marker prima di ritentare (§5):
// mai consultato da quel percorso, solo dal trigger automatico.
let _statsErrorKeys = null;         // Set<"fantacalcioId:seasonKey">

// Chiamata PRIMA di disposeActiveAuctionDetailResources(), ad ogni ingresso in
// renderAuctionDetailPage(): resetta cache/stagione/token SOLO se l'auctionId è cambiato rispetto
// all'ultimo proprietario noto — una asta A non deve MAI silenziosamente diventare la cache di
// un'asta B (A7-P1-1D2 §9). Un render strutturale della STESSA asta non tocca nulla qui.
function ensureStatsLifecycleForAuction(auctionId){
  if(_statsCacheOwnerAuctionId !== auctionId){
    _statsCacheOwnerAuctionId = auctionId;
    _statsSeasonResolved = false;
    _statsSeasonKey = null;
    _statsCache = new Map();
    _statsErrorKeys = new Set();
    _statsPreviewToken = 0;
  }
}

function disposeActiveAuctionDetailResources(){
  _renderGeneration++;
  clearNominationCountdown();
  if(_activeLiveController){
    // Round A7-P1-6F — interrogato SEMPRE PRIMA di stop(), mai dopo: getActivityHandoff() legge
    // solo chiusure già vive del controller, stop() non le invalida, ma l'ordine resta esplicito e
    // a prova di refactor futuro. Sovrascrive qualunque handoff precedente ancora presente — quello
    // apparteneva a un controller ormai già sostituito da questo stesso, mai utile oltre quel punto.
    _lastActivityHandoff = _activeLiveController.getActivityHandoff();
    _activeLiveController.stop();
    _activeLiveController = null;
  }
}

// Round alpha.7 (A7-P1-1, A7-P1-1D2 §9): wrapper del cleanup esposto ad app.js — SOLO per la
// navigazione FUORI da questa pagina, mai richiamato internamente dall'inizio di
// renderAuctionDetailPage() (che chiama disposeActiveAuctionDetailResources() da sola, invariata,
// esattamente come prima di questo round — altrimenti la cache statistiche verrebbe azzerata ad
// OGNI render strutturale della stessa asta, violando A7-P1-1D2 §8). Un ritorno a QUALUNQUE asta
// (anche la stessa appena lasciata) riparte quindi da una cache statistiche pulita.
function disposeAuctionDetailPageForNavigation(){
  disposeActiveAuctionDetailResources();
  _statsCacheOwnerAuctionId = null;
  // Round A7-P1-6F — stesso principio del reset statistiche sopra: una navigazione FUORI da questa
  // pagina non deve mai lasciare un handoff dell'asta appena lasciata come baseline silenziosa per
  // una qualunque asta successiva (anche la stessa, se riaperta: un cold-render pieno la ri-seminerà
  // comunque da una lettura reale, mai da questo handoff residuo).
  _lastActivityHandoff = null;
  _statsSeasonResolved = false;
  _statsSeasonKey = null;
  _statsCache = null;
  _statsErrorKeys = null;
  _statsPreviewToken = 0;
  _opponentExpansionOwnerAuctionId = null;
  _expandedOpponentSeatIds = null;
}

// Round A7-P1-3 (§9, correzione esplicita): stato di espansione "rosa avversario" — ciclo di vita
// DEDICATO, mai accoppiato a quello delle statistiche giocatore sopra (_statsCacheOwnerAuctionId
// resta di competenza esclusiva del pannello statistiche, un concetto del tutto indipendente che
// solo per coincidenza condivide la stessa forma di ciclo di vita). Stesso SPIRITO — sopravvive a un
// render strutturale della STESSA asta (mai toccato da disposeActiveAuctionDetailResources(), che
// gira ad ogni render strutturale), si azzera SOLO al cambio di auctionId o alla navigazione fuori da
// questa pagina — ma variabili/funzioni proprie, mai condivise/riusate da quell'altro sottosistema.
let _opponentExpansionOwnerAuctionId = null;
let _expandedOpponentSeatIds = null;   // Set<seatId> — seat correntemente espanse nella sezione opponent intelligence

// Chiamata PRIMA di disposeActiveAuctionDetailResources(), ad ogni ingresso in
// renderAuctionDetailPage() — stesso principio di ensureStatsLifecycleForAuction() sopra, ma per il
// SOLO stato di espansione: resetta il Set SOLO se l'auctionId è cambiato rispetto all'ultimo
// proprietario noto. Un render strutturale della stessa asta (es. dopo un settlement) non tocca nulla
// qui — è esattamente questo il comportamento richiesto da A7-P1-3 §9/§17 (l'espansione di un
// opponent deve sopravvivere a un settlement altrove nella stessa asta).
function ensureOpponentExpansionLifecycleForAuction(auctionId){
  if(_opponentExpansionOwnerAuctionId !== auctionId){
    _opponentExpansionOwnerAuctionId = auctionId;
    _expandedOpponentSeatIds = new Set();
  }
}

/**
 * @param {string|null} [justGeneratedCode] - codice in chiaro appena generato/ruotato da QUESTA
 *   stessa sessione di rendering, da mostrare una sola volta (mai persistito — vedi
 *   MULTIPLAYER_SESSION_DESIGN_alpha4_r1.md §9/§5). Un successivo render (navigazione, refresh
 *   manuale, qualunque altra azione) non lo ripropone mai: solo il chiamante che ha appena ricevuto
 *   la risposta di rotateJoinCode() può passarlo qui.
 * @param {string|null} [justFinalizedOutcome] - outcome ("unsold"|"pending_settlement") appena
 *   restituito da QUESTA stessa chiamata a finalizeIfExpired(), round alpha.5-r4-r2 (BLOCKER 5,
 *   audit indipendente). Usato SOLO per mostrare un messaggio di transizione distinto per "unsold"
 *   ("Nomina conclusa: giocatore invenduto.") nel render immediatamente successivo — mai persistito,
 *   un refresh/navigazione successivi non lo ripropongono mai. Il caso pending_settlement non ne ha
 *   bisogno: quello stato resta persistito e visibile a ogni render finché non viene risolto.
 * @param {{playerFantacalcioId:number, seatId:string, amount:number}|null} [justSettledInfo] - esito
 *   appena restituito da QUESTA stessa chiamata a settleNomination(), round alpha.5-r5-r1. Usato SOLO
 *   per mostrare la conferma "Acquistato" nel render immediatamente successivo — mai persistito: il
 *   ledger acquisti (purchases) resta comunque visibile in ogni render successivo tramite
 *   listAuctionPurchases(), questo flag serve solo per il messaggio di conferma one-shot.
 * @param {Array|undefined} [prefetchedNominations] - round A7-P1-6 (§10/§11/§12 del design): array
 *   GIÀ ottenuto da una lettura di listAuctionNominations() riuscita in QUESTO stesso tick del poll
 *   (mai un valore stale/carried-forward — il chiamante in startLivePolling() lo passa SOLO quando
 *   `nominationsFetchedFreshThisTick` è vero) — se presente, sostituisce il fetch che questa funzione
 *   farebbe altrimenti (DUPLICATE_NOMINATION_FETCH_PER_TICK=0). undefined per ogni altra chiamata
 *   (cold-render iniziale, navigazione, post-mutazione, o un fallback dopo un fallimento del poll) —
 *   in quei casi questa funzione esegue sempre il proprio fetch, invariato.
 */
export async function renderAuctionDetailPage(container, ctx, auctionId, justGeneratedCode, justFinalizedOutcome, justSettledInfo, prefetchedNominations){
  // Round alpha.7 (A7-P1-1D2 §9): valutato PRIMA di dispose() e prima di qualunque altra cosa —
  // resetta la cache/stagione statistiche SOLO se questo render riguarda un'asta diversa da quella
  // attualmente proprietaria dello stato modulo-locale (mai per un render strutturale della stessa
  // asta, che è invece il caso comune di questa stessa chiamata).
  ensureStatsLifecycleForAuction(auctionId);
  // Round A7-P1-3 §9: ciclo di vita DEDICATO, chiamato qui allo stesso punto per lo stesso motivo
  // (prima di dispose(), prima di qualunque altra cosa) ma mai la stessa funzione/variabili di quello
  // statistiche sopra — vedi il commento esteso su ensureOpponentExpansionLifecycleForAuction().
  ensureOpponentExpansionLifecycleForAuction(auctionId);
  // Round alpha.5-r4-r1 / esteso alpha.6 P0.2-A (CORREZIONE A, §5-bis): qualunque countdown E
  // qualunque controller di polling live attivi dal render PRECEDENTE vanno disposti PRIMA di
  // qualunque altra cosa — mai due interval/controller sovrapposti dopo una sequenza rapida di
  // azioni/mutazioni (questa stessa funzione è richiamata direttamente, senza passare da app.js, dopo
  // ogni mutazione riuscita — vedi le chiamate più sotto).
  disposeActiveAuctionDetailResources();
  // CORREZIONE R: catturato SUBITO dopo dispose() — dispose() ha appena incrementato
  // _renderGeneration, quindi questo È il valore corrente per QUESTA esecuzione; un dispose()
  // successivo (da un altro render interno più recente, o dal cleanup esposto ad app.js) lo rende
  // stale, verificabile ad ogni ripresa asincrona sotto.
  const myGeneration = _renderGeneration;
  // Round PB-H3 (pre-Beta hardening), estesa da PB-H5: catturato QUI, prima di qualunque await,
  // esattamente come hashAtEntry in DashboardPage.js. Un retry esposto da una card di errore di
  // ingresso richiama renderAuctionDetailPage() DIRETTAMENTE (bypassando il coalescer di app.js,
  // come il retry della Dashboard): finché nessuna lettura di ingresso ha mai completato con
  // successo un render intero per QUESTA invocazione, nessun cleanup è mai stato registrato presso
  // il coalescer, che perciò non chiama disposeActiveAuctionDetailResources() (e non incrementa
  // _renderGeneration) quando l'utente naviga altrove (back/forward/URL bar — nessun link resta nel
  // DOM durante il placeholder di caricamento) mentre una lettura è ancora pendente. myGeneration da
  // solo non rileverebbe questo caso; un hash diverso da quello di ingresso resta comunque prova
  // sufficiente che una destinazione più recente ha già preso il posto di questo container.
  const hashAtEntry = window.location.hash;
  // PB-H5 (audit PB-H5D2): predicato LOCALE unico, condiviso da OGNI punto di ripresa asincrona fra
  // qui e la prima scrittura completa e normale della pagina (container.innerHTML più sotto) — mai
  // un'infrastruttura globale/di modulo, mai un secondo canale. Prima di PB-H5 solo le due catch di
  // getAuctionSummary()/loadAuction() controllavano anche l'hash; ogni altro punto di ripresa
  // (incluse le continuazioni di SUCCESSO di quelle stesse due letture) controllava solo
  // myGeneration — un successo tardivo dopo la navigazione (mai solo un rigetto tardivo) sfuggiva
  // quindi al controllo esattamente allo stesso modo di un fallimento, perché nessun retry di
  // lettura di ingresso registra mai un cleanup presso il coalescer finché non arriva in fondo con
  // successo. Ogni punto di ripresa da qui in poi, fino alla scrittura finale, usa questo stesso,
  // unico predicato — mai una copia locale della sua espressione.
  function isStalePreFirstPaint(){
    return myGeneration !== _renderGeneration || window.location.hash !== hashAtEntry;
  }
  container.innerHTML = '<p class="hint">Caricamento asta…</p>';
  // Round Beta-P3-4 (design D/D2, accettato): catch NARROW, dedicato SOLO a getCurrentUser() — mai
  // condiviso con getAuctionSummary() sotto, la cui semantica di fallimento (renderAuctionEntryErrorCard,
  // invariata) resta un problema distinto. Un rigetto qui è un'incertezza sulla sessione, mai un
  // "non autenticato" (vedi BackendAdapter.js/SupabaseBackendAdapter.js) — nessun redirect a /login;
  // riusa lo STESSO isStalePreFirstPaint() già stabilito da PB-H5, mai una seconda guardia.
  let user;
  try{
    user = await ctx.adapter.getCurrentUser();
  }catch(err){
    if(isStalePreFirstPaint()) return;
    container.innerHTML = renderAuctionAuthUncertainCard();
    wireAuctionAuthUncertainCard(container, ctx, auctionId);
    return;
  }
  if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5): stale — nessun redirect, nessuna scrittura
  if(!user){ ctx.navigate("/login"); return; }

  // Round PB-H3 (pre-Beta hardening): catch ristretto alla SOLA lettura getAuctionSummary() — mai il
  // ramo "Asta non trovata" sotto (logica di valore risolto normalmente, mai un'eccezione, resta
  // fuori da questo catch carattere per carattere) e mai getCurrentUser() sopra/il resto della
  // funzione sotto. Un fallimento qui (rete/backend, mai un bug applicativo) produce una card
  // dedicata con retry invece di un rigetto non gestito che lascia l'utente bloccato su "Caricamento
  // asta…" per sempre — ogni ALTRA eccezione resta un rigetto genuino, mai assorbita.
  let summary;
  try{
    summary = await ctx.adapter.getAuctionSummary(auctionId);
  }catch(err){
    if(isStalePreFirstPaint()) return;
    container.innerHTML = renderAuctionEntryErrorCard();
    wireAuctionEntryErrorCard(container, ctx, auctionId);
    return;
  }
  if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5): anche la continuazione di SUCCESSO va guardata
  if(!summary){
    container.innerHTML = '<p class="errtext">Asta non trovata (o non hai accesso).</p><p><a href="#/dashboard">Torna alle mie aste</a></p>';
    return;
  }
  // PB-H3: catch ristretto alla SOLA lettura loadAuction() — stessa card di errore/retry di sopra
  // (l'utente non deve distinguere QUALE delle due letture sia fallita: un retry richiama per intero
  // renderAuctionDetailPage(), che ripete entrambe da capo).
  let envelope;
  try{
    envelope = await ctx.repository.loadAuction(auctionId);
  }catch(err){
    if(isStalePreFirstPaint()) return;
    container.innerHTML = renderAuctionEntryErrorCard();
    wireAuctionEntryErrorCard(container, ctx, auctionId);
    return;
  }
  if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)

  let bodyHtml;
  let needsInitForm = false;
  if(!envelope.state){
    if(summary.myRole === "owner"){
      needsInitForm = true;
      bodyHtml = renderInitialSetupForm();
    } else {
      bodyHtml = '<p class="hint">Questa asta non ha ancora uno stato salvato. Solo il proprietario può inizializzarla.</p>';
    }
  } else if(envelope.state.schemaOrigin === "astalab-1.3"){
    const raw = envelope.state.raw || {};
    const nPlayers = Array.isArray(raw.players) ? raw.players.length : 0;
    const legaName = (raw.config && raw.config.nomeLega) || "—";
    bodyHtml =
      '<div class="card">' +
        "<h3>Contenuto importato da AstaLab 1.3</h3>" +
        "<p>Lega: <strong>" + escapeHtml(legaName) + "</strong></p>" +
        "<p>Giocatori nel backup: <strong>" + nPlayers + "</strong></p>" +
        "<p>Versione dati di origine: <strong>" + escapeHtml(envelope.state.sourceDataVersion==null?"—":String(envelope.state.sourceDataVersion)) + "</strong></p>" +
        "<p>Importato il: <strong>" + escapeHtml(envelope.state.importedAt||"—") + "</strong></p>" +
      "</div>";
  } else if(envelope.state.schemaOrigin === "astalab-2.0"){
    const raw = envelope.state.raw || {};
    const cfg = raw.config || {};
    const nPlayers = Array.isArray(raw.players) ? raw.players.length : 0;
    bodyHtml =
      '<div class="card">' +
        "<h3>Configurazione iniziale</h3>" +
        "<p>Partecipanti: <strong>" + escapeHtml(cfg.partecipanti==null?"—":String(cfg.partecipanti)) + "</strong></p>" +
        "<p>Budget per squadra: <strong>" + escapeHtml(cfg.budget==null?"—":String(cfg.budget)) + " crediti</strong></p>" +
        "<p>Ripartizione budget: <strong>" + escapeHtml(raw.budgetPresetKey||"—") + "</strong></p>" +
        "<p>Slot per ruolo: <strong>POR " + escapeHtml(String(cfg.slotPOR)) + " · DIF " + escapeHtml(String(cfg.slotDIF)) +
          " · CEN " + escapeHtml(String(cfg.slotCEN)) + " · ATT " + escapeHtml(String(cfg.slotATT)) + "</strong></p>" +
        "<p>Giocatori in rosa: <strong>" + nPlayers + "</strong></p>" +
        '<p class="hint">Configurata il: ' + escapeHtml(envelope.state.createdAt||"—") + "</p>" +
      "</div>";
  } else {
    bodyHtml = '<div class="card"><h3>Stato salvato</h3><pre class="mono">' + escapeHtml(JSON.stringify(envelope.state, null, 2).slice(0,2000)) + "</pre></div>";
  }

  let members = [];
  let membersError = null;
  try{ members = await ctx.repository.listAuctionMembers(auctionId); }
  catch(err){ membersError = err && err.message ? err.message : String(err); }
  if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)

  // PB-H5 (design PB-H5D §3/§6): getJoinCodeStatus() è una dipendenza SOLO di sezione — non è mai un
  // input di deriveAuctionProductState()/lifecycleNow/liveInteractionAllowed, mai parte della somma
  // di hasSummaryReadError più sotto. Un fallimento qui degrada SOLO la card "Codice di invito"
  // dentro renderLobbySection(), esattamente come membersError/seatsError sopra/sotto — mai la card
  // di errore di ingresso a pagina intera (quella resta riservata alle letture AUTORITATIVE:
  // getAuctionSummary/loadAuction/loadLiveSession). Nessun pulsante di retry dedicato: un futuro
  // render completo la ritenta naturalmente, stesso principio già in uso per membersError/seatsError.
  let joinCodeStatus = null;
  let joinCodeStatusError = null;
  if(summary.myRole === "owner"){
    let statusResult;
    try{ statusResult = await ctx.repository.getJoinCodeStatus(auctionId); }
    catch(err){ joinCodeStatusError = err && err.message ? err.message : String(err); }
    if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5): copre SIA il successo SIA il fallimento appena catturato
    if(statusResult && statusResult.ok) joinCodeStatus = statusResult;
  }

  // Round alpha.5-r1: squadre (seat) + lifecycle della sessione live — vedi
  // LIVE_AUCTION_DESIGN_alpha5_r1.md §13. Sola lettura per il member (nessun controllo di
  // assegnazione mostrato); l'owner vede anche i comandi di creazione/assegnazione/lifecycle.
  let seats = [];
  let seatsError = null;
  try{ seats = await ctx.repository.listAuctionSeats(auctionId); }
  catch(err){ seatsError = err && err.message ? err.message : String(err); }
  if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
  // PB-H5 (design PB-H5D §3/§4/§5): loadLiveSession() è AUTORITATIVA per l'intera pagina —
  // lifecycleNow/deriveAuctionProductState()/liveInteractionAllowed dipendono tutti direttamente dal
  // suo valore risolto, e un null legittimo ("nessuna sessione live mai persistita", cioè 'setup')
  // ha un significato preciso che un fallimento di lettura non deve MAI simulare. Catch ristretto
  // alla SOLA lettura — stessa card di errore/retry di ingresso di getAuctionSummary()/loadAuction()
  // sopra (l'utente non deve distinguere QUALE lettura di ingresso sia fallita: il retry richiama
  // per intero renderAuctionDetailPage(), che ripete tutte le letture di ingresso da capo).
  let liveSession;
  try{
    liveSession = await ctx.repository.loadLiveSession(auctionId);
  }catch(err){
    if(isStalePreFirstPaint()) return;
    container.innerHTML = renderAuctionEntryErrorCard();
    wireAuctionEntryErrorCard(container, ctx, auctionId);
    return;
  }
  if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5): anche la continuazione di SUCCESSO va guardata

  // Round alpha.6 P0.2-A1 (CORREZIONE Q): reference per-render minima al più recente snapshot
  // server-authoritative della sessione live — inizializzata dal cold-render, poi aggiornata dal
  // controller di polling (onNonStructuralSnapshot, più sotto) PRIMA che l'utente possa costruire il
  // comando successivo. Letta al momento del submit da wireNominationSection() per placeBid(), MAI
  // dal `liveSession` catturato in questa closure (che resterebbe l'istantanea del cold-render anche
  // dopo un rival bid applicato via patch non strutturale — bidSeq/currentNominationId altrimenti
  // stale). Mai un nuovo framework: un solo oggetto mutabile `{ current }`, stesso spirito già in uso
  // per `_activeLiveController`/`_nominationCountdownDeadlineMs` sopra.
  const currentLiveSnapshotRef = { current: liveSession };

  // Round A7-P1-5 — storico offerte della SOLA nomina corrente (mai l'intero storico dell'asta,
  // §12/HISTORY_FETCH_SCOPE=CURRENT_NOMINATION_ONLY): stessa condizione esatta usata da
  // renderNominationSection() per decidere se una nomina è effettivamente "corrente" (lifecycle
  // running + nominationStatus non nullo) — nessun fetch se quella condizione è falsa
  // (HISTORY_FETCH_WITHOUT_CURRENT_NOMINATION=NO). Amendment 3 (round A7-P1-5): arricchimento NON
  // critico — un fallimento qui non deve MAI interrompere il render della pagina, mai propagarsi ai
  // controlli di mutazione/bidding/polling, che restano un ramo di lettura completamente
  // indipendente (seats/purchases/liveSession sopra) — isolato nel proprio try/catch, mai un
  // secondo canale di staleness: usa isStalePreFirstPaint() come ogni altro punto di ripresa qui.
  let bidHistory = [];
  let bidHistoryError = false;
  const currentNominationIsActive = !!(liveSession && liveSession.lifecycle === "running" && liveSession.nominationStatus && liveSession.currentNominationId != null);
  if(currentNominationIsActive){
    try{ bidHistory = await ctx.repository.listBidsForNomination(auctionId, liveSession.currentNominationId); }
    catch(err){ bidHistory = []; bidHistoryError = true; }
    if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
  }

  // Round alpha.5-r4-r1: regole live + catalogo corrente (per risolvere nome/ruolo del giocatore
  // nominato e per popolare il selettore di nomina) — solo quando la sezione live è effettivamente
  // rilevante (setup con owner, o running), mai un fetch inutile del catalogo per un'asta ancora priva
  // di stato/senza sessione live.
  const lifecycleNow = liveSession ? liveSession.lifecycle : "setup";
  // A7-P1-2 §11: prima di questo round questi dati venivano caricati SOLO per lifecycle='running',
  // facendo sparire budget/rosa/riepilogo non appena la sessione veniva messa in pausa o chiusa
  // (A7-P1-2D §5, difetto UX confermato). "hasEverStarted" copre running|paused|closed — il riepilogo
  // di completamento deve restare disponibile in tutti e tre questi stati grezzi, indipendentemente
  // dallo stato di prodotto derivato che ne risulta.
  const hasEverStarted = lifecycleNow === "running" || lifecycleNow === "paused" || lifecycleNow === "closed";
  let liveRules = null;
  let rosterRules = null;
  let catalogPlayers = [];
  let purchases = [];
  let nominations = [];
  let rosterRulesError = false;
  let purchasesError = false;
  let catalogPlayersError = false;
  let nominationsError = false;
  if(lifecycleNow === "setup" || hasEverStarted){
    liveRules = await ctx.repository.loadLiveRules(auctionId).catch(()=>null);
    if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
    // Round alpha.5-r5-r1: regole di rosa/budget — stesso prerequisito di liveRules, caricate insieme.
    // A7-P1-2 §25: un fallimento è tracciato esplicitamente (mai solo azzerato in silenzio) — serve a
    // decidere se il riepilogo di completamento può essere mostrato come autoritativo o deve mostrare
    // data-completion-summary-state="error" invece di un successo con numeri parziali.
    try{ rosterRules = await ctx.repository.loadRosterRules(auctionId); }
    catch(err){ rosterRules = null; rosterRulesError = true; }
    if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
    if(hasEverStarted){
      try{ catalogPlayers = await ctx.catalogRepository.listCurrentPlayers(); }
      catch(err){ catalogPlayers = []; catalogPlayersError = true; }
      if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
      // budget/roster/riepilogo sono SEMPRE derivati da qui (loadRosterRules + listAuctionPurchases),
      // mai un campo mutabile duplicato — vedi deriveAuctionProductState()/renderOpponentIntelligenceSection()/
      // renderCompletionSummarySection() sotto.
      try{ purchases = await ctx.repository.listAuctionPurchases(auctionId); }
      catch(err){ purchases = []; purchasesError = true; }
      if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
      // Round A7-P1-6 (§10 del design) — riusa il prefetch fresco del poll quando disponibile, MAI
      // un secondo fetch di rete per lo stesso tick (DUPLICATE_NOMINATION_FETCH_PER_TICK=0). Il
      // chiamante (onStructuralTransition, sopra) garantisce che `prefetchedNominations` sia
      // undefined ogni volta che il fetch di quel tick non è stato sia tentato sia riuscito — questo
      // ramo esegue quindi sempre il proprio fetch quando quella garanzia non vale (cold-render
      // iniziale, navigazione, o un fallback dopo un fallimento — round D3 §11/§12).
      if(prefetchedNominations !== undefined){
        nominations = prefetchedNominations;
      } else {
        try{ nominations = await ctx.repository.listAuctionNominations(auctionId); }
        catch(err){
          // Round A7-P1-6F (fix del "double-failure handoff", A7-P1-6V) — MAI più un [] fabbricato
          // qui quando un handoff reale esiste: _lastActivityHandoff.nominations è l'ultimo array
          // GENUINAMENTE noto (dal controller appena fermato da disposeActiveAuctionDetailResources()
          // sopra, catturato PRIMA che questo stesso fetch fosse anche tentato) — un doppio
          // fallimento (poll + questo fallback) non deve mai far regredire la baseline nomine a
          // zero quando N era già realmente conosciuto. Se nessun handoff esiste (vero cold-render
          // iniziale, mai un controller precedente per questa pagina), [] resta l'unico valore
          // disponibile — ma initialActivityContentUnknown più sotto garantisce comunque che questo
          // NON venga mai trattato come "zero nomine confermate" dal prossimo controller.
          nominations = (_lastActivityHandoff && Array.isArray(_lastActivityHandoff.nominations)) ? _lastActivityHandoff.nominations : [];
          nominationsError = true;
        }
        if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
      }
      // Round alpha.7 (A7-P1-1D2 §9): risolta esattamente UNA volta per proprietario auctionId
      // (_statsSeasonResolved), MAI ri-risolta da un successivo render strutturale della stessa
      // asta — vedi ensureStatsLifecycleForAuction() sopra. Solo per lifecycle='running': il pannello
      // statistiche esiste solo in una nomina attiva, mai rilevante per un riepilogo terminale.
      if(lifecycleNow === "running" && !_statsSeasonResolved){
        const targetSeasons = await ctx.catalogRepository.getStatisticsTargetSeasons().catch(()=>[]);
        if(isStalePreFirstPaint()) return;   // CORREZIONE R (PB-H5)
        _statsSeasonKey = (targetSeasons && targetSeasons[0]) || null;
        _statsSeasonResolved = true;
      }
    }
  }

  // A7-P1-2 §12: unico punto di calcolo per completezza/anomalia rosa e stato di prodotto
  // (active/complete/closed-incomplete/roster-anomaly) — mai duplicato fra le sezioni che lo
  // consumano. Sicuro da chiamare anche con rosterRules=null/purchases=[] (es. 'setup', o un
  // fallimento di lettura): ricade su "active" per costruzione (hasParticipants richiede sia
  // partecipanti sia rosterRules caricate).
  const derivedState = deriveAuctionProductState(seats, rosterRules, purchases, liveSession, lifecycleNow);
  const hasSummaryReadError = hasEverStarted && (!!seatsError || !!membersError || rosterRulesError || purchasesError || catalogPlayersError);
  // A7-P1-2A/A2 §5/§6 (correzione: il commento precedente qui era insufficientemente conservativo — la
  // guardia RPC lato server resta sempre l'autorità finale sulla CORRETTEZZA, ma questo da solo non
  // basta a giustificare la sicurezza della PRESENTAZIONE/dell'interazione dal vivo). Se una lettura
  // necessaria al riepilogo fallisce, derivedState.state è calcolato da dati NECESSARIAMENTE incompleti
  // (es. purchases=[] dopo un fallimento di listAuctionPurchases()) e potrebbe quindi ricadere su
  // "active" anche quando lo stato persistito reale era già terminale — mostrare di nuovo i controlli
  // di mutazione IN PIÙ continuare a interrogare il server con il polling in quel caso sarebbero
  // entrambi una regressione di sicurezza PRESENTATA (mai una regressione di correttezza, dato che la
  // RPC rifiuterebbe comunque una mutazione, ma un'interfaccia che promette un'azione dal vivo che
  // l'utente non dovrebbe nemmeno percepire come disponibile). liveInteractionAllowed è quindi UN SOLO
  // flag indipendente e condiviso — mai due condizioni sottilmente diverse per controlli e polling —
  // sempre false quando una lettura di riepilogo fallisce; usato sia per i controlli di mutazione sia
  // per la condizione di mount del polling (§4-quinquies, sotto). data-auction-state resta invece il
  // miglior calcolo onesto possibile dai dati realmente disponibili (mai forzato a un valore inventato
  // solo perché una lettura è fallita) — la sicurezza vive in liveInteractionAllowed, non in quel
  // valore.
  const liveInteractionAllowed = derivedState.state === "active" && !hasSummaryReadError;

  // CORREZIONE R (PB-H5): cancello finale obbligatorio — nessun side effect (DOM finale, wiring,
  // countdown, controller) può avvenire oltre questo punto se questo render è stato reso stale da
  // un dispose() concorrente O da una navigazione senza dispose (retry diretto) durante uno
  // qualunque degli await sopra.
  if(isStalePreFirstPaint()) return;

  container.innerHTML =
    '<div class="auction-detail" data-auction-state="' + derivedState.state + '">' +
      '<p><a href="#/dashboard">← Torna alle mie aste</a></p>' +
      "<h1>" + escapeHtml(summary.name) + "</h1>" +
      '<p class="hint">Stagione: ' + escapeHtml(summary.season||"—") + " · Stato: " + escapeHtml(summary.status) +
        " · Revisione stato: " + envelope.revision + "</p>" +
      bodyHtml +
      renderCompletionSummarySection(summary, seats, members, rosterRules, purchases, catalogPlayers, liveSession, derivedState, hasSummaryReadError) +
      renderLobbySection(summary, members, membersError, joinCodeStatus, joinCodeStatusError, justGeneratedCode, seats, lifecycleNow, user.id) +
      renderLiveAuctionSection(summary, members, seats, seatsError, liveSession, user.id, liveInteractionAllowed) +
      renderLiveRulesSection(summary, liveRules, lifecycleNow) +
      renderRosterRulesSection(summary, rosterRules, lifecycleNow) +
      renderNominationSection(summary, seats, liveSession, liveRules, catalogPlayers, catalogPlayersError, purchases, purchasesError, user.id, justFinalizedOutcome, justSettledInfo, liveInteractionAllowed, bidHistory, bidHistoryError) +
      renderOpponentIntelligenceSection(derivedState.perSeat, purchases, catalogPlayers, members, user.id, liveInteractionAllowed, lifecycleNow) +
      renderActivityFeedSection(nominations, purchases, catalogPlayers, seats, hasEverStarted, nominationsError) +
    "</div>";

  if(needsInitForm) wireInitialSetupForm(container, ctx, auctionId, envelope.revision);
  wireLobbySection(container, ctx, auctionId, summary);
  wireLiveAuctionSection(container, ctx, auctionId, summary);
  // A7-P1-3 §8: puramente client-side, nessun await — vedi wireOpponentIntelligenceSection() sopra.
  wireOpponentIntelligenceSection(container);
  wireLiveRulesSection(container, ctx, auctionId, liveRules);
  wireRosterRulesSection(container, ctx, auctionId, rosterRules);
  wireNominationSection(container, ctx, auctionId, seats, liveSession, user.id, currentLiveSnapshotRef, catalogPlayers, catalogPlayersError, purchases, purchasesError);
  wireCompletionSummarySection(container, ctx, auctionId);

  // Round alpha.7 (A7-P1-1 §13): statistiche della nomina attiva — SOLO da questo cold/full-render,
  // MAI da applyNonStructuralLiveSnapshot()/dal poll (§13/§20: LIVE_POLL_STATS_FETCH_COUNT=0).
  // Guardato da _renderGeneration esattamente come ogni altra ripresa asincrona di questa funzione:
  // una risposta tardiva non ridipinge mai un render ormai superato (A7-P1-1D2 §12) — ma popola
  // comunque la cache condivisa, utile per il prossimo che chiede la stessa chiave.
  if(liveSession && liveSession.lifecycle === "running" && liveSession.nominationStatus && liveSession.nominatedPlayerFantacalcioId != null){
    const activeFantacalcioId = liveSession.nominatedPlayerFantacalcioId;
    const activeSeasonKey = _statsSeasonKey;
    if(hasKnownStatsError(activeFantacalcioId, activeSeasonKey)){
      // Round A7-P1-1A §4: un fallimento è già noto per QUESTA chiave in QUESTA stessa asta — un
      // render strutturale automatico (bid/nomina/poll-triggered) non deve MAI ritentare da sé;
      // ridipinge lo stato di errore già noto, zero nuove richieste al repository. L'unico modo per
      // uscirne è un'interazione esplicita dell'utente (mai possibile qui: la nomina attiva non è
      // ri-selezionabile) o una nuova nomina/candidato (chiave diversa, marker indipendente).
      repaintStatsPanel(container, "nominationStatsPanel", "error", null);
    } else {
      fetchPlayerStatsCached(ctx, catalogPlayers, activeFantacalcioId, activeSeasonKey)
        .then(stats => {
          if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
          repaintStatsPanel(container, "nominationStatsPanel", stats ? "ready" : "empty", stats);
        })
        .catch(() => {
          if(myGeneration !== _renderGeneration) return;
          repaintStatsPanel(container, "nominationStatsPanel", "error", null);
        });
    }
  }

  // Round alpha.6 P0.2-A (§5-bis/CORREZIONE F): CONTROLLER_MOUNTED per ogni lifecycle diverso da
  // "closed" — mai solo "running" (altrimenti una pagina aperta durante 'setup'/'paused' non
  // rileverebbe mai da sola una transizione remota verso 'running', §5-bis). initialSnapshot
  // (§4-ter/CORREZIONE G) riusa liveSession/purchases già caricati sopra — nessun nuovo fetch
  // dedicato. viewerContext (§4-quater/CORREZIONE M) deriva da mySeat, calcolato qui una volta con lo
  // stesso predicato già usato da renderNominationSection/wireNominationSection (riga 641 storica).
  // A7-P1-2 §22/§14 (design D2/D3): la condizione di mount ora è lo stato di prodotto derivato, mai
  // più solo lifecycle!=='closed' — uno stato "complete"/"roster-anomaly" può verificarsi anche con
  // lifecycle ancora 'running'/'paused' (il completamento è un segnale derivato, indipendente da
  // lifecycle), e per costruzione nulla può più cambiare in quegli stati (guardia RPC 0016 per
  // roster-anomaly/complete, lifecycle='closed' per closed-incomplete) — continuare a interrogare il
  // server sarebbe puro spreco, mai un beneficio reale per l'utente.
  // CORREZIONE round A7-P1-2A2: questa condizione dipendeva SOLO da derivedState.state === "active",
  // stesso difetto già corretto per i controlli di mutazione in A7-P1-2A (§5/§6) ma mai propagato qui
  // — un'asta persistita realmente terminale il cui riepilogo fallisce a leggersi poteva ricadere su
  // "active" (dati necessariamente incompleti) e continuare a interrogare il server dal vivo, la stessa
  // regressione di sicurezza presentata, solo per il polling invece che per i controlli. Riusa
  // ESATTAMENTE lo stesso predicato condiviso liveInteractionAllowed, mai una seconda condizione
  // sottilmente diversa scritta apposta per il polling.
  if(liveInteractionAllowed){
    // Round Beta-P3-5 (design D2/D3, accettato): flag di sola closure, mai un timer/una nuova
    // infrastruttura — vive per l'intera durata di QUESTA istanza del controller di polling
    // (ri-creata da zero a ogni render intero, incluso ogni renderAuctionDetailPage() interno
    // innescato da onStructuralTransition sotto). Azzerata da qualunque tick riuscito (non
    // strutturale O strutturale), impostata al primo onError() di una sequenza di fallimenti
    // consecutivi — nessun log per i fallimenti successivi della STESSA sequenza.
    let livePollFailureStreakLogged = false;
    const mySeat = seats.find(s => s.assignedUserId === user.id) || null;
    const initialSnapshot = {
      lifecycle: liveSession ? liveSession.lifecycle : "setup",
      revision: liveSession ? liveSession.revision : 0,
      nominationStatus: liveSession ? liveSession.nominationStatus : null,
      currentNominationId: liveSession ? liveSession.currentNominationId : null,
      nominatedPlayerFantacalcioId: liveSession ? liveSession.nominatedPlayerFantacalcioId : null,
      nominatingSeatId: liveSession ? liveSession.nominatingSeatId : null,
      currentHighestBid: liveSession ? liveSession.currentHighestBid : null,
      highestBidderSeatId: liveSession ? liveSession.highestBidderSeatId : null,
      bidSeq: liveSession ? liveSession.bidSeq : 0,
      deadlineAt: liveSession ? liveSession.deadlineAt : null,
      purchases: purchases,
      // Round A7-P1-5: baseline coerente con quanto appena renderizzato dal cold-render sopra —
      // il primo tick del poll (se la revision non cambia) riusa questi stessi valori invariati,
      // mai un fetch immediato superfluo (§14, HISTORY_FETCH_ON_UNCHANGED_REVISION=NO).
      bidHistory: currentNominationIsActive ? bidHistory : null,
      bidHistoryNominationId: currentNominationIsActive ? liveSession.currentNominationId : null,
      bidHistoryError: currentNominationIsActive ? bidHistoryError : false,
      // Round A7-P1-6 (§5/§7 del design): seminata dal conteggio REALE già letto dal cold-render
      // sopra — mai zero. Un'asta con K nomine storiche deve iniziare con lastAppliedSnapshot.nominations
      // di lunghezza K, altrimenti il primo tick del poll produrrebbe un falso positivo strutturale
      // (0 -> K) anche per un semplice bid altrui (INITIAL_EXISTING_NOMINATIONS_CAUSE_FALSE_STRUCTURAL=NO).
      nominations: nominations
    };
    _activeLiveController = startLivePolling({
      auctionId,
      repository: ctx.repository,
      initialSnapshot,
      lifecycle: lifecycleNow,
      viewerContext: { mySeatId: mySeat ? mySeat.id : null },
      // Round A7-P1-6F (fix del "double-failure handoff", A7-P1-6V) — `nominationsError` true
      // significa che il fetch nomine di QUESTO stesso render (fresco o di fallback) NON è
      // riuscito: il contenuto appena costruito sopra (initialSnapshot.nominations) non riflette
      // mai una lettura riuscita, quale che sia il suo valore (l'ultimo handoff noto, o [] se
      // nessun handoff esisteva) — il nuovo controller deve quindi ritentare ad ogni tick
      // indipendentemente dalla revision, E il primo fetch riuscito deve forzare comunque un render
      // completo per popolare davvero il contenuto reale (mai una patch leggera su un contenuto
      // mai stato corretto).
      initialActivityStale: nominationsError,
      initialActivityContentUnknown: nominationsError,
      onNonStructuralSnapshot(snapshot){
        livePollFailureStreakLogged = false;   // round Beta-P3-5: un tick riuscito azzera la sequenza
        applyNonStructuralLiveSnapshot(container, seats, snapshot, currentLiveSnapshotRef);
      },
      onStructuralTransition(snapshot){
        // §4-bis: un solo full authoritative render tramite il percorso ESISTENTE — mai un secondo
        // percorso di rendering scritto ad hoc per il poll. Rivaluta da sé lifecycleNow/listCurrentPlayers/ecc.
        // Round A7-P1-6 (§10/§11/§12 del design): riusa `snapshot.nominations` come prefetch SOLO
        // quando `snapshot.nominationsFetchedFreshThisTick` è vero — mai una nomination array stale
        // spacciata per fresca quando la transizione è stata innescata da un ALTRO termine mentre il
        // fetch nomine di QUESTO tick è fallito (round D3 §8/§11 — il render, in quel caso, esegue da
        // sé il proprio fallback, invariato).
        livePollFailureStreakLogged = false;   // round Beta-P3-5: tick riuscito (anche strutturale)
        const prefetchedNominations = snapshot && snapshot.nominationsFetchedFreshThisTick ? snapshot.nominations : undefined;
        renderAuctionDetailPage(container, ctx, auctionId, undefined, undefined, undefined, prefetchedNominations);
      },
      onError(e){
        // §9: un singolo errore transitorio di poll non deve mai eseguire logout/distruggere la
        // pagina/sostituire lo snapshot valido con dati vuoti — l'ultimo stato applicato resta a
        // schermo invariato, nessuna azione UI invasiva (comportamento invariato da questo round).
        //
        // Round Beta-P3-5 (design D2/D3, accettato): SOLO il primo onError() di una sequenza di
        // fallimenti consecutivi produce un log diagnostico — mai uno per ogni tick fallito (onError
        // spara ad ogni tick fallito finché la sequenza persiste, vedi livePollingController.js §9).
        // Nessuna modifica a livePollingController.js: `e` è già passato da onError(e) lì (riga 401),
        // solo mai consumato qui prima d'ora.
        if(!livePollFailureStreakLogged){
          livePollFailureStreakLogged = true;
          logClientError({ area:"auction-detail", operation:"live-poll", error:e, context:{ auctionId } });
        }
      }
    });
  }

  // Round alpha.6 P0.2-A (§5-bis): riferimento stabile alla funzione modulo-locale, mai un closure
  // legato al controller creato in QUESTA specifica chiamata — app.js la invoca una sola volta, al
  // momento della navigazione fuori da questa pagina, ma quella chiamata legge sempre
  // _activeLiveController CORRENTE (§5-bis, "Il cleanup esposto ad app.js...").
  // Round alpha.7 (A7-P1-1D2 §9): il wrapper disposeAuctionDetailPageForNavigation() — non più
  // disposeActiveAuctionDetailResources() da sola — cosicché la navigazione fuori da questa pagina
  // resetti ESPLICITAMENTE anche la cache/stagione statistiche, mai solo un render strutturale.
  return disposeAuctionDetailPageForNavigation;
}

// Round alpha.6 P0.2-A (§8): patch mirata per un NON_STRUCTURAL_SNAPSHOT_CHANGE — tocca SOLO i campi
// di sola lettura del contratto (offerta corrente, miglior offerente, countdown/finalizeBtn), MAI il
// markup di #bidForm/#bidAmount (§8 punto 3/CORREZIONE D) — chiamata solo dal controller di polling,
// mai dal cold-render (che scrive questi stessi valori una volta, dentro renderNominationSection()).
// Round alpha.6 P0.2-A1 (CORREZIONE Q): PRIMA di qualunque tocco al DOM, aggiorna
// `currentLiveSnapshotRef.current` al nuovo snapshot — cosicché un submit di #bidForm che avvenga
// anche solo un istante dopo legga sempre `currentNominationId`/`bidSeq` server-authoritative più
// recenti, mai quelli catturati dal cold-render. Assegna una NUOVA reference (mai una mutazione in
// place dell'oggetto restituito dall'adapter): `snapshot` qui è già l'oggetto merge-ato costruito dal
// controller (`{...raw, purchases}`, mai l'oggetto grezzo originale), quindi questa riga non muta mai
// nulla che l'adapter possieda ancora.
export function applyNonStructuralLiveSnapshot(container, seats, snapshot, currentLiveSnapshotRef){
  currentLiveSnapshotRef.current = snapshot;

  const bidText = container.querySelector("#currentHighestBidText");
  if(bidText){
    bidText.textContent = snapshot.currentHighestBid == null ? "nessuna" : (snapshot.currentHighestBid + " crediti");
  }

  const highestText = container.querySelector("#highestBidderText");
  if(highestText){
    const highestSeat = snapshot.highestBidderSeatId ? seats.find(s => s.id === snapshot.highestBidderSeatId) : null;
    highestText.textContent = highestSeat ? highestSeat.label : "—";
  }

  const countdownEl = container.querySelector("#nominationCountdown");
  const finalizeBtn = container.querySelector("#finalizeNominationBtn");
  if(countdownEl && snapshot.deadlineAt){
    countdownEl.setAttribute("data-deadline", snapshot.deadlineAt);
    applyNominationCountdownDeadline(snapshot.deadlineAt, countdownEl, finalizeBtn);
  }

  applyBidHistoryLightPatch(container, seats, snapshot);
  applyActivityFeedErrorLightPatch(container, snapshot);
}

// Round A7-P1-6 (§14/§20 del design D2/D3) — patch leggera dedicata SOLO all'indicatore di
// errore/stale del feed attività: mai una ricostruzione delle righe (quelle restano di esclusiva
// competenza del render strutturale completo — un aumento del conteggio nomine è SEMPRE strutturale,
// §9, quindi questa patch leggera non viene mai attraversata in quel caso: la sostituzione arriva
// naturalmente dal render strutturale). Qui si gestisce SOLO il caso "stesso conteggio": un fallimento
// di lettura (snapshot.activityError) mostra l'errore senza toccare le righe già presenti; un
// recupero a conteggio invariato (snapshot.activityRecovered) lo rimuove — mai l'inverso, mai un
// azzeramento implicito quando né l'uno né l'altro sono veri in questo giro (§20: RETAIN fino a un
// esito esplicito).
function applyActivityFeedErrorLightPatch(container, snapshot){
  const feedEl = container.querySelector("[data-auction-activity-feed]");
  if(!feedEl) return;
  if(snapshot.activityError){
    feedEl.setAttribute("data-auction-activity-state", "error");
    const errorMsgEl = feedEl.querySelector("[data-auction-activity-error-msg]");
    if(errorMsgEl) errorMsgEl.hidden = false;
    const emptyMsgEl = feedEl.querySelector("[data-auction-activity-empty-msg]");
    if(emptyMsgEl) emptyMsgEl.hidden = true;
    return;
  }
  if(snapshot.activityRecovered){
    const listEl = feedEl.querySelector(".activity-feed-list");
    const isEmpty = !listEl || listEl.children.length === 0;
    feedEl.setAttribute("data-auction-activity-state", isEmpty ? "empty" : "ready");
    const errorMsgEl = feedEl.querySelector("[data-auction-activity-error-msg]");
    if(errorMsgEl) errorMsgEl.hidden = true;
    const emptyMsgEl = feedEl.querySelector("[data-auction-activity-empty-msg]");
    if(emptyMsgEl) emptyMsgEl.hidden = !isEmpty;
  }
}

// Round A7-P1-5 (§15/§16/§22) — patch leggera dedicata SOLO allo storico offerte: non ricostruisce
// mai la card nomina, aggiunge SOLO le righe mancanti (dedup per data-bid-history-entry, mai una
// riga duplicata — §19) mantenendo l'ordine bidSeq già garantito dall'array in ingresso. Guardia
// anti-nomina-obsoleta (§16): applica SOLO se la history ricevuta riguarda ESATTAMENTE la nomina
// attualmente montata nel DOM — un fetch per una nomina già chiusa/sostituita da un render
// strutturale concorrente non deve mai scrivere sopra la card corrente. Se [data-bid-history] non
// esiste (nessuna nomina corrente quando questo tick è partito, o già rimossa da un render
// strutturale nel frattempo) non fa nulla — mai un errore, mai una creazione dal nulla: solo il
// render strutturale/cold-render possiede la creazione iniziale di questo blocco.
function applyBidHistoryLightPatch(container, seats, snapshot){
  const historyEl = container.querySelector("[data-bid-history]");
  if(!historyEl) return;
  if(historyEl.getAttribute("data-bid-history-nomination") !== String(snapshot.bidHistoryNominationId)) return;

  const errorMsgEl = historyEl.querySelector("[data-bid-history-error-msg]");
  const emptyMsgEl = historyEl.querySelector("[data-bid-history-empty-msg]");
  const listEl = historyEl.querySelector(".bid-history-list");

  if(snapshot.bidHistoryError){
    // §22: un fallimento di QUESTO giro mostra l'errore ma non tocca mai le righe già renderizzate
    // con successo — una ripresa (giro successivo riuscito) le ritrova intatte, zero re-render.
    historyEl.setAttribute("data-bid-history-state", "error");
    if(errorMsgEl) errorMsgEl.hidden = false;
    if(emptyMsgEl) emptyMsgEl.hidden = true;
    return;
  }
  if(!Array.isArray(snapshot.bidHistory)) return;

  const isEmpty = snapshot.bidHistory.length === 0;
  historyEl.setAttribute("data-bid-history-state", isEmpty ? "empty" : "ready");
  if(errorMsgEl) errorMsgEl.hidden = true;
  if(emptyMsgEl) emptyMsgEl.hidden = !isEmpty;
  if(!listEl) return;

  const existingIds = new Set([...listEl.querySelectorAll("[data-bid-history-entry]")].map(li => li.getAttribute("data-bid-history-entry")));
  snapshot.bidHistory.forEach(b => {
    if(existingIds.has(String(b.id))) return;   // §19: mai una riga duplicata per un id già presente
    const seat = seats.find(s => s.id === b.seatId);
    const li = document.createElement("li");
    li.setAttribute("data-bid-history-entry", String(b.id));
    li.setAttribute("data-bid-history-seat", String(b.seatId));
    li.setAttribute("data-bid-history-seq", String(b.bidSeq));
    li.textContent = (seat ? seat.label : "—") + " — " + b.amount + " crediti";
    listEl.appendChild(li);
  });
}

// Round PB-H3 (pre-Beta hardening): card di errore dedicata per un fallimento delle DUE letture di
// ingresso (getAuctionSummary()/loadAuction()) — deliberatamente distinta, sia nel marcatore
// strutturale sia nel testo, dalla card "Asta non trovata" esistente (quella riflette un valore
// risolto normalmente — nessun accesso/nessuna asta — mai un'eccezione di rete/backend).
function renderAuctionEntryErrorCard(){
  return (
    '<div class="card" data-auction-entry-error>' +
      "<h3>Impossibile caricare l'asta</h3>" +
      '<p class="errtext">Non è stato possibile caricare i dati di questa asta. Riprova più tardi.</p>' +
      '<button type="button" class="btn ghost sm" data-auction-entry-retry>Riprova</button>' +
      '<p><a href="#/dashboard">Torna alle mie aste</a></p>' +
    "</div>"
  );
}

// Round Beta-P3-7 (design D/D2, accettato): UNICO punto da cui OGNI retry di questa pagina (i 5 siti
// difensivi già instrumentati da Beta-P3-5) richiama il render completo — mai duplicato per
// ciascuno. Riusa la _renderGeneration ESISTENTE (mai un secondo contatore, P3-7D2 §10), già
// incrementata da disposeActiveAuctionDetailResources() come primissima istruzione di OGNI chiamata
// a renderAuctionDetailPage(). Cattura hash+generazione SUBITO DOPO l'invocazione, mai prima
// (P3-7D2 §5-6: un valore catturato prima verrebbe invalidato dall'incremento di QUESTO STESSO
// tentativo, rendendo il confronto sempre "stale" per costruzione — la vecchia motivazione qui
// sopra, "garantiscono da soli", presupponeva questo esattamente al contrario). `operation`
// preserva l'identità del log P3-5 già accettata per il sito che ha avviato il tentativo, riusata
// invariata anche per ogni successivo tentativo dal fallback generico sotto (stessa lineage, mai
// un'etichetta nuova). Il log diagnostico avviene SEMPRE, anche per un tentativo ormai stale; solo
// la scrittura del fallback è condizionata al possesso ancora attuale della route.
function attemptAuctionDetailReload(container, ctx, auctionId, operation){
  return async () => {
    const hashAtRetryStart = window.location.hash;
    const renderPromise = renderAuctionDetailPage(container, ctx, auctionId);
    const attemptedGeneration = _renderGeneration;
    try{
      await renderPromise;
    }catch(e){
      // Difesa in profondità: renderAuctionDetailPage() non lancia mai per un fallimento delle
      // letture già gestite internamente (ciascuna già try/catch-ata o già ridotta a un flag
      // d'errore) — questo ramo copre solo un imprevisto realmente eccezionale, mai atteso in
      // condizioni normali.
      logClientError({ area:"auction-detail", operation, error:e, context:{ auctionId } });
      const stale = _renderGeneration !== attemptedGeneration || window.location.hash !== hashAtRetryStart;
      if(stale) return;
      renderAuctionDetailSecondaryFallback(container, ctx, auctionId, operation);
    }
  };
}

// Round Beta-P3-7: card GENERICA per un fallimento inatteso del render completo di retry — mai una
// delle card di primo livello (entry/auth-uncertain/members/seats/completion-summary), che restano
// invariate per il loro esito già gestito: questa non pretende di sapere quale dei cinque tentativi
// abbia fallito né quale dipendenza sia coinvolta, solo che il ricaricamento stesso non è riuscito.
// Il suo stesso pulsante richiama di nuovo lo stesso helper — riarmato indefinitamente, mai un
// pulsante disabilitato in modo permanente.
function renderAuctionDetailSecondaryFallback(container, ctx, auctionId, operation){
  container.innerHTML =
    '<div class="card" data-auction-secondary-fallback>' +
      "<h3>Impossibile ricaricare</h3>" +
      '<p class="errtext">Non è stato possibile ricaricare questa pagina. Riprova.</p>' +
      '<button type="button" class="btn ghost sm" data-auction-secondary-retry>Riprova</button>' +
      '<p><a href="#/dashboard">Torna alle mie aste</a></p>' +
    "</div>";
  const retryBtn = container.querySelector("[data-auction-secondary-retry]");
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptAuctionDetailReload(container, ctx, auctionId, operation)();
  });
}

// PB-H3: retry = richiama per intero renderAuctionDetailPage() tramite l'helper condiviso sopra.
function wireAuctionEntryErrorCard(container, ctx, auctionId){
  const retryBtn = container.querySelector("[data-auction-entry-retry]");
  if(!retryBtn) return;
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptAuctionDetailReload(container, ctx, auctionId, "entry-retry")();
  });
}

// Round Beta-P3-4: percorso DISTINTO da renderAuctionEntryErrorCard() sopra — un'incertezza sulla
// sessione, mai "sessione scaduta"/"disconnesso"/"account non valido" (non provate dalla sola
// incertezza di lettura).
function renderAuctionAuthUncertainCard(){
  return (
    '<div class="card" data-auction-auth-uncertain>' +
      "<h3>Impossibile verificare la sessione</h3>" +
      '<p class="errtext">Non è stato possibile verificare la tua sessione. Riprova.</p>' +
      '<button type="button" class="btn ghost sm" data-auction-auth-uncertain-retry>Riprova</button>' +
      '<p><a href="#/dashboard">Torna alle mie aste</a></p>' +
    "</div>"
  );
}
function wireAuctionAuthUncertainCard(container, ctx, auctionId){
  const retryBtn = container.querySelector("[data-auction-auth-uncertain-retry]");
  if(!retryBtn) return;
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptAuctionDetailReload(container, ctx, auctionId, "auth-uncertain-retry")();
  });
}

function renderLobbySection(summary, members, membersError, joinCodeStatus, joinCodeStatusError, justGeneratedCode, seats, lifecycle, myUserId){
  const isOwner = summary.myRole === "owner";
  // A7-P1-8 (D3 §4/§8/§12/§13, migrazione 0017 parte B): fuori da 'setup' (inclusa 'closed', D3 §8
  // Model C1), un membro che controlla ATTUALMENTE una seat assegnata non può abbandonare/essere
  // rimosso — l'unico modo per liberarlo è la sostituzione atomica del controller
  // (renderSeatOwnerControls più sotto). Nessun riferimento a nomine/offerte/acquisti qui: la regola
  // dipende SOLO da lifecycle e assegnazione corrente, mai da storico (D3: "History is intentionally
  // irrelevant under the final D3 model").
  const assignedUserIds = new Set((seats||[]).filter(s => s.assignedUserId).map(s => s.assignedUserId));
  const departureFrozen = lifecycle !== "setup";
  // Round Beta-P2-2: membersError resta una dipendenza SOLO di sezione — mai un input di
  // deriveAuctionProductState()/lifecycleNow/liveInteractionAllowed (invariato) — ma blocca
  // realmente l'assegnazione/sostituzione di seat (renderSeatOwnerControls ha bisogno della member
  // list per popolare i propri <select>), quindi merita un retry, non solo un messaggio statico.
  // Nessuna nuova condizione di lifecycle: il retry compare esattamente dove/quando il ramo di
  // errore esistente compare già, mai un nuovo caso in cui la sezione stessa non renderizzerebbe.
  const rows = membersError
    ? ('<div data-members-read-error>' +
        '<p class="errtext">' + escapeHtml(membersError) + "</p>" +
        '<button type="button" class="btn ghost sm" data-members-read-retry>Riprova</button>' +
      "</div>")
    : (
      '<div class="table-wrap"><table><thead><tr><th>Partecipante</th><th>Ruolo</th><th>Entrato il</th><th></th></tr></thead><tbody>' +
      members.map(m => {
        const blockRemove = departureFrozen && assignedUserIds.has(m.userId);
        const removeBtn = (isOwner && m.role!=="owner")
          ? ('<button class="btn ghost sm" data-remove-member="' + escapeHtml(m.userId) + '"' +
              (blockRemove ? ' disabled title="Riassegna prima la squadra a un altro membro."' : "") +
              '>Rimuovi</button>')
          : "";
        return "<tr>" +
          "<td>" + escapeHtml(m.displayName) + "</td>" +
          '<td><span class="badge ' + (m.role==="owner"?"ok":"neutral") + '">' + escapeHtml(m.role) + "</span></td>" +
          "<td>" + escapeHtml(fmtDate(m.joinedAt)) + "</td>" +
          "<td>" + removeBtn + "</td>" +
        "</tr>";
      }).join("") +
      "</tbody></table></div>"
    );

  const myRoleLine = '<p class="hint">Il tuo ruolo in questa asta: <strong>' + escapeHtml(summary.myRole) + "</strong></p>";

  let joinCodeBlock = "";
  if(isOwner){
    // PB-H5 (design PB-H5D §6/§7): la precedenza dell'errore viene PRIMA di qualunque
    // interpretazione normale di joinCodeStatus — un fallimento di lettura non deve MAI apparire
    // come uno stato legittimo "nessun codice" (che mostrerebbe un'affordance "Genera codice" priva
    // di senso: l'owner potrebbe già avere un codice attivo, semplicemente non ancora letto con
    // successo). Nessun pulsante qui, nemmeno di retry dedicato: un futuro render completo della
    // pagina la ritenta naturalmente da capo, stesso principio già in uso per membersError/seatsError.
    if(joinCodeStatusError){
      joinCodeBlock =
        '<div class="card">' +
          "<h3>Codice di invito</h3>" +
          '<p class="errtext">' + escapeHtml(joinCodeStatusError) + "</p>" +
        "</div>";
    } else {
      const hasActive = joinCodeStatus && joinCodeStatus.active;
      joinCodeBlock =
        '<div class="card">' +
          "<h3>Codice di invito</h3>" +
          (justGeneratedCode
            ? '<p class="okmsg mono">' + escapeHtml(formatJoinCodeForDisplay(canonicalizeJoinCode(justGeneratedCode))) +
              ' <button class="btn ghost sm" id="copyJoinCodeBtn" data-code="' + escapeHtml(justGeneratedCode) + '">Copia</button></p>' +
              '<p class="hint">Questo codice viene mostrato una sola volta: se lo perdi, genera/ruota di nuovo.</p>'
            : '<p class="hint">Stato: <span class="badge ' + (hasActive?"ok":"neutral") + '">' + (hasActive ? "attivo" : "nessun codice") + "</span>" +
              (hasActive && joinCodeStatus.generatedAt ? " · generato il " + escapeHtml(fmtDate(joinCodeStatus.generatedAt)) : "") + "</p>"
          ) +
          '<div class="flex gap8 wrap">' +
            '<button class="btn primary sm" id="rotateJoinCodeBtn">' + (hasActive ? "Ruota codice" : "Genera codice") + "</button>" +
            (hasActive ? '<button class="btn ghost sm" id="revokeJoinCodeBtn">Revoca</button>' : "") +
          "</div>" +
          '<div id="joinCodeMsg"></div>' +
        "</div>";
    }
  }

  const blockLeave = departureFrozen && assignedUserIds.has(myUserId);
  const leaveBlock = !isOwner
    ? ('<div class="card"><button class="btn ghost sm" id="leaveAuctionBtn"' +
        (blockLeave ? ' disabled title="Non puoi abbandonare l\'asta mentre controlli una squadra assegnata. Chiedi al proprietario di sostituirti."' : "") +
        '>Abbandona asta</button></div>')
    : "";

  return (
    '<div class="card">' +
      "<h3>Partecipanti</h3>" +
      myRoleLine +
      rows +
      '<div id="lobbyMsg"></div>' +
    "</div>" +
    joinCodeBlock +
    leaveBlock
  );
}

function wireLobbySection(container, ctx, auctionId, summary){
  // Round alpha.6 P0.2-A1 (CORREZIONE R): catturato UNA VOLTA, al momento del wiring — QUESTO è il
  // render generation a cui appartiene ogni handler qui sotto. Se una RPC di mutazione resta pending
  // mentre l'utente naviga altrove (app.js chiama il cleanup esposto → disposeActiveAuctionDetailResources()
  // → _renderGeneration incrementato), il controllo subito prima del re-render impedisce di
  // resuscitare la pagina asta su un `container` che nel frattempo appartiene a un'altra route.
  const myGeneration = _renderGeneration;
  // Round Beta-P2-2: retry di SOLA lettura per listAuctionMembers() — mai una mutazione. Stesso
  // principio dei retry a pagina intera già stabiliti (PB-H3/PB-H5): richiama per intero
  // renderAuctionDetailPage(), che ripete TUTTE le letture di ingresso da capo (se anche seatsError
  // era presente, questo stesso retry può risolvere entrambi). myGeneration qui sopra è già
  // sufficiente — nessun controllo di hash aggiuntivo: questo pulsante esiste solo dopo che un
  // render intero è già riuscito almeno una volta, quindi un cleanup è già registrato presso il
  // coalescer di app.js, esattamente come per ogni altro handler di mutazione in questa funzione.
  const membersRetryBtn = container.querySelector("[data-members-read-retry]");
  if(membersRetryBtn){
    membersRetryBtn.addEventListener("click", async () => {
      membersRetryBtn.disabled = true;
      // Round Beta-P3-7: guardia PRECEDENTE invariata — evita di AVVIARE un retry ridondante se il
      // render che possiede questo pulsante è già superato. L'helper condiviso sotto aggiunge il
      // controllo COMPLEMENTARE, dopo il fallimento del retry stesso (P3-7D2): un render più
      // recente può prendere il posto di questo ANCHE mentre il retry è in volo, non solo prima.
      if(myGeneration !== _renderGeneration) return;
      await attemptAuctionDetailReload(container, ctx, auctionId, "members-retry")();
    });
  }
  const rotateBtn = container.querySelector("#rotateJoinCodeBtn");
  if(rotateBtn){
    rotateBtn.addEventListener("click", async () => {
      rotateBtn.disabled = true;
      const result = await ctx.repository.rotateJoinCode(auctionId);
      if(!result.ok){
        const msg = container.querySelector("#joinCodeMsg");
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile generare il codice.") + "</p>";
        rotateBtn.disabled = false;
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId, result.code);
    });
  }
  const revokeBtn = container.querySelector("#revokeJoinCodeBtn");
  if(revokeBtn){
    revokeBtn.addEventListener("click", async () => {
      revokeBtn.disabled = true;
      const result = await ctx.repository.revokeJoinCode(auctionId);
      if(!result.ok){
        const msg = container.querySelector("#joinCodeMsg");
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile revocare il codice.") + "</p>";
        revokeBtn.disabled = false;
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  }
  const copyBtn = container.querySelector("#copyJoinCodeBtn");
  if(copyBtn){
    copyBtn.addEventListener("click", async () => {
      const code = copyBtn.getAttribute("data-code");
      try{
        if(navigator && navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(code);
      }catch(e){ /* clipboard non disponibile in questo ambiente: l'utente può comunque selezionare/copiare il testo a mano */ }
    });
  }
  // Round Beta-P3-1 (design Beta-P3-1D §5/§10): window.confirm() PRIMA di qualunque mutazione, mai
  // dopo il disable esistente — un annullamento non deve alterare lo stato del pulsante. blockRemove/
  // P1-8/l'autorità server-side restano invariati: un pulsante già disabled da blockRemove non
  // raggiunge mai questo handler (il click su un elemento disabled non spara mai l'evento).
  container.querySelectorAll("[data-remove-member]").forEach(btn=>{
    btn.addEventListener("click", async () => {
      if(!window.confirm("Vuoi rimuovere questo membro dall'asta? Per rientrare dovrà usare nuovamente un codice valido e l'eventuale assegnazione dovrà essere ripristinata.")) return;
      const memberUserId = btn.getAttribute("data-remove-member");
      btn.disabled = true;
      const result = await ctx.repository.removeAuctionMember(auctionId, memberUserId);
      const msg = container.querySelector("#lobbyMsg");
      if(!result.ok){
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile rimuovere il membro.") + "</p>";
        btn.disabled = false;
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  });
  const leaveBtn = container.querySelector("#leaveAuctionBtn");
  if(leaveBtn){
    leaveBtn.addEventListener("click", async () => {
      if(!window.confirm("Vuoi uscire da questa asta? Per rientrare dovrai usare nuovamente un codice valido.")) return;
      leaveBtn.disabled = true;
      const result = await ctx.repository.leaveAuction(auctionId);
      if(!result.ok){
        leaveBtn.disabled = false;
        const msg = container.querySelector("#lobbyMsg");
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile abbandonare l'asta.") + "</p>";
        return;
      }
      ctx.navigate("/dashboard");
    });
  }
}

// ---------------------------------------------------------------------
// Round alpha.5-r1: squadre (seat) + lifecycle della sessione live. Vedi
// LIVE_AUCTION_DESIGN_alpha5_r1.md §13 — UI minimale, non cosmetica. L'owner assegna/rimuove membri
// dalle seat tramite un selettore che elenca SOLO i membri correnti dell'asta (mai un input libero di
// userId); il member vede la stessa lista in sola lettura, con la propria seat evidenziata. Nessuna UI
// di nomina/offerta in questo round: quelle RPC non sono implementate (§8 del design doc) — mostrare
// un controllo che chiamerebbe un'RPC inesistente sarebbe un'interfaccia bugiarda.
// ---------------------------------------------------------------------
const LIFECYCLE_LABELS = { setup:"in preparazione", running:"in corso", paused:"in pausa", closed:"chiusa" };

function renderLiveAuctionSection(summary, members, seats, seatsError, liveSession, myUserId, liveInteractionAllowed){
  const isOwner = summary.myRole === "owner";
  const lifecycle = liveSession ? liveSession.lifecycle : "setup";
  const revision = liveSession ? liveSession.revision : 0;

  // BLOCKER C (audit indipendente su alpha.5-r1): listAuctionSeats() non restituisce più un nome di
  // visualizzazione (profiles è self-only, mai apribile globalmente) — il nome si risolve QUI,
  // cercando assignedUserId nella member list già ottenuta dalla RPC sicura listAuctionMembers().
  // Se assignedUserId è non-null ma non si trova nella member list (caso limite, mai atteso in
  // condizioni normali — RLS/RPC lo impediscono strutturalmente), NON si mostra mai "non assegnata"
  // (che affermerebbe il falso: la seat È assegnata) — si mostra invece un'etichetta onesta che
  // segnala l'incoerenza senza inventare un nome.
  const membersById = new Map(members.map(m => [m.userId, m]));
  // A7-P1-8 (D3 §16/§21, migrazione 0017): usato SOLO per calcolare i candidati validi alla
  // sostituzione del controller di una seat già occupata — un membro che controlla già un'altra seat
  // non è un candidato valido (il backend lo rifiuterebbe comunque, one-seat-per-member).
  const assignedUserIds = new Set(seats.filter(s => s.assignedUserId).map(s => s.assignedUserId));
  // Round Beta-P2-2: seatsError resta una dipendenza SOLO di sezione — mai un input di
  // deriveAuctionProductState()/lifecycleNow/liveInteractionAllowed (invariato) — ma blocca
  // realmente canStart (sotto: seats.length>0 con seats=[] su un fallimento) e la sostituzione del
  // controller (assignedUserIds). Nessuna nuova condizione di lifecycle: il retry compare
  // esattamente dove/quando il ramo di errore esistente compare già.
  const seatRows = seatsError
    ? ('<div data-seats-read-error>' +
        '<p class="errtext">' + escapeHtml(seatsError) + "</p>" +
        '<button type="button" class="btn ghost sm" data-seats-read-retry>Riprova</button>' +
      "</div>")
    : (
      '<div class="seats-table-wrap"><table><thead><tr><th>#</th><th>Squadra</th><th>Assegnata a</th>' + (isOwner ? "<th></th>" : "") + "</tr></thead><tbody>" +
      (seats.length ? seats.map(s => {
        const isMine = s.assignedUserId && s.assignedUserId === myUserId;
        let assignedLabel;
        if(!s.assignedUserId){
          assignedLabel = '<span class="hint">non assegnata</span>';
        } else {
          const member = membersById.get(s.assignedUserId);
          assignedLabel = (member ? escapeHtml(member.displayName) : '<span class="hint">membro (nome non disponibile)</span>') +
            (isMine ? ' <span class="badge ok">tu</span>' : "");
        }
        const ownerControls = isOwner ? renderSeatOwnerControls(s, members, lifecycle, assignedUserIds) : "";
        return '<tr' + (isMine ? ' class="mine"' : "") + '>' +
          "<td>" + s.seatNo + "</td>" +
          "<td>" + escapeHtml(s.label) + "</td>" +
          "<td>" + assignedLabel + "</td>" +
          (isOwner ? "<td>" + ownerControls + "</td>" : "") +
        "</tr>";
      }).join("") : '<tr><td colspan="' + (isOwner?4:3) + '" class="hint">Nessuna squadra creata.</td></tr>') +
      "</tbody></table></div>"
    );

  // A7-P1-8 (D3 §16/§19): create_auction_seat resta congelata fuori da 'setup' (0013, invariata) —
  // il form non deve restare visibile+cliccabile solo per mostrare l'errore backend (D3 §19).
  const createSeatBlock = (isOwner && lifecycle === "setup")
    ? '<form id="createSeatForm" class="flex gap8 wrap">' +
        '<input type="text" id="newSeatLabel" placeholder="Nome squadra" required maxlength="80">' +
        '<button type="submit" class="btn ghost sm">+ Aggiungi squadra</button>' +
      "</form>"
    : "";

  const canStart = seats.length > 0;
  // A7-P1-2 §23: nessun controllo di lifecycle (avvia/pausa/riprendi/chiudi) per uno stato di prodotto
  // diverso da "active" — uno stato terminale (complete/closed-incomplete/roster-anomaly) non deve mai
  // esporre un'azione che il backend potrebbe comunque rifiutare o che non ha più senso proporre.
  const lifecycleControls = isOwner && liveInteractionAllowed ? (
    '<div class="flex gap8 wrap">' +
      (lifecycle === "setup" ? '<button class="btn primary sm" id="startLiveBtn"' + (canStart?"":" disabled") + '>Avvia sessione live</button>' : "") +
      (lifecycle === "running" ? '<button class="btn ghost sm" id="pauseLiveBtn">Metti in pausa</button>' : "") +
      (lifecycle === "paused" ? '<button class="btn primary sm" id="resumeLiveBtn">Riprendi</button>' : "") +
      ((lifecycle === "running" || lifecycle === "paused") ? '<button class="btn ghost sm" id="closeLiveBtn">Chiudi sessione</button>' : "") +
    "</div>" +
    (!canStart && lifecycle === "setup" ? '<p class="hint">Crea almeno una squadra prima di avviare la sessione live.</p>' : "")
  ) : "";

  return (
    '<div class="card">' +
      "<h3>Squadre</h3>" +
      seatRows +
      createSeatBlock +
      '<div id="seatMsg"></div>' +
    "</div>" +
    '<div class="card">' +
      "<h3>Sessione live</h3>" +
      '<p class="hint">Stato: <span class="badge ' + (lifecycle==="running"?"ok":"neutral") + '">' + escapeHtml(LIFECYCLE_LABELS[lifecycle]||lifecycle) + "</span> · revisione " + revision + "</p>" +
      lifecycleControls +
      '<div id="liveSessionMsg"></div>' +
    "</div>"
  );
}

// A7-P1-8 (D3 §16/§20/§21/§22, migrazione 0017): la topologia (crea/elimina) e l'attivazione di una
// seat vuota (null→B) restano congelate fuori da 'setup' — nessun controllo per queste due azioni
// fuori da 'setup' (D3 §19: niente resta cliccabile solo per mostrare un rifiuto backend). La sola
// mutazione ammessa fuori da 'setup' è la sostituzione atomica del controller di una seat GIÀ
// occupata (A→B) — riusa lo STESSO <select data-assign-seat-select>/<button data-assign-seat> e lo
// STESSO handler già esistente in wireLiveAuctionSection() per l'assegnazione di una seat vuota: la
// RPC sottostante (assignMemberToSeat) è la stessa chiamata in entrambi i casi, la distinzione
// null-vs-occupata vive lato server (migrazione 0017 parte A), mai duplicata qui (D3 §22: "Use the
// EXISTING repository operation... No new repository method"). data-seat-replace-control marca
// esclusivamente questa variante (occupata, fuori/dentro 'setup') per i test — il click-handling resta
// identico.
function renderSeatOwnerControls(seat, members, lifecycle, assignedUserIds){
  if(lifecycle === "closed") return "";
  if(seat.assignedUserId){
    const replaceCandidates = members.filter(m => m.userId !== seat.assignedUserId && !assignedUserIds.has(m.userId));
    const replaceOptions = replaceCandidates.map(m =>
      '<option value="' + escapeHtml(m.userId) + '">' + escapeHtml(m.displayName) + " (" + escapeHtml(m.role) + ")</option>"
    ).join("");
    const replaceControl =
      '<span data-seat-replace-control="' + escapeHtml(seat.id) + '">' +
        '<select data-assign-seat-select="' + escapeHtml(seat.id) + '"><option value="">sostituisci con…</option>' + replaceOptions + "</select> " +
        '<button class="btn ghost sm" data-assign-seat="' + escapeHtml(seat.id) + '">Sostituisci</button>' +
      "</span>";
    if(lifecycle !== "setup") return replaceControl;
    return replaceControl + " " +
      '<button class="btn ghost sm" data-unassign-seat="' + escapeHtml(seat.id) + '">Rimuovi assegnazione</button> ' +
      '<button class="btn ghost sm" data-delete-seat="' + escapeHtml(seat.id) + '" disabled title="Rimuovi prima l\'assegnazione">Elimina</button>';
  }
  if(lifecycle !== "setup") return "";
  const options = members.map(m =>
    '<option value="' + escapeHtml(m.userId) + '">' + escapeHtml(m.displayName) + " (" + escapeHtml(m.role) + ")</option>"
  ).join("");
  return (
    '<select data-assign-seat-select="' + escapeHtml(seat.id) + '"><option value="">assegna a…</option>' + options + "</select> " +
    '<button class="btn ghost sm" data-assign-seat="' + escapeHtml(seat.id) + '">Assegna</button> ' +
    '<button class="btn ghost sm" data-delete-seat="' + escapeHtml(seat.id) + '">Elimina</button>'
  );
}

function wireLiveAuctionSection(container, ctx, auctionId, summary){
  // Round alpha.6 P0.2-A1 (CORREZIONE R) — vedi wireLobbySection() sopra per la motivazione completa.
  const myGeneration = _renderGeneration;
  // Round Beta-P2-2: retry di SOLA lettura per listAuctionSeats() — vedi il commento gemello su
  // membersRetryBtn in wireLobbySection() per la motivazione completa (stesso principio, stesso
  // myGeneration, nessun hash aggiuntivo).
  const seatsRetryBtn = container.querySelector("[data-seats-read-retry]");
  if(seatsRetryBtn){
    seatsRetryBtn.addEventListener("click", async () => {
      seatsRetryBtn.disabled = true;
      // Round Beta-P3-7: guardia PRECEDENTE invariata — vedi il commento gemello in
      // wireLobbySection() per la motivazione completa.
      if(myGeneration !== _renderGeneration) return;
      await attemptAuctionDetailReload(container, ctx, auctionId, "seats-retry")();
    });
  }
  const createSeatForm = container.querySelector("#createSeatForm");
  if(createSeatForm){
    createSeatForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const input = container.querySelector("#newSeatLabel");
      const label = input.value;
      const msg = container.querySelector("#seatMsg");
      const result = await ctx.repository.createAuctionSeat(auctionId, label);
      if(!result.ok){
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile creare la squadra.") + "</p>";
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  }
  container.querySelectorAll("[data-assign-seat]").forEach(btn => {
    btn.addEventListener("click", async () => {
      const seatId = btn.getAttribute("data-assign-seat");
      const select = container.querySelector('[data-assign-seat-select="' + CSS.escape(seatId) + '"]');
      const userId = select ? select.value : "";
      const msg = container.querySelector("#seatMsg");
      if(!userId){
        if(msg) msg.innerHTML = '<p class="errtext">Seleziona un membro da assegnare.</p>';
        return;
      }
      btn.disabled = true;
      const result = await ctx.repository.assignMemberToSeat(seatId, userId);
      if(!result.ok){
        btn.disabled = false;
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile assegnare la squadra.") + "</p>";
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  });
  container.querySelectorAll("[data-unassign-seat]").forEach(btn => {
    btn.addEventListener("click", async () => {
      const seatId = btn.getAttribute("data-unassign-seat");
      btn.disabled = true;
      const result = await ctx.repository.unassignMemberFromSeat(seatId);
      const msg = container.querySelector("#seatMsg");
      if(!result.ok){
        btn.disabled = false;
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile rimuovere l'assegnazione.") + "</p>";
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  });
  container.querySelectorAll("[data-delete-seat]:not([disabled])").forEach(btn => {
    btn.addEventListener("click", async () => {
      const seatId = btn.getAttribute("data-delete-seat");
      btn.disabled = true;
      const result = await ctx.repository.deleteAuctionSeat(seatId);
      const msg = container.querySelector("#seatMsg");
      if(!result.ok){
        btn.disabled = false;
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile eliminare la squadra.") + "</p>";
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  });

  // confirmMessage: round Beta-P3-1 (design Beta-P3-1D §7/§11) — SOLO closeLiveBtn la passa. Un
  // parametro opzionale, mai un ramo separato duplicato: start/pause/resume restano invariati byte
  // per byte (nessuna conferma, operazioni live time-sensitive già classificate Group C). Il check
  // resta la primissima riga, PRIMA del disable esistente — un annullamento non deve mai alterare lo
  // stato del pulsante.
  async function runLifecycleCommand(btnId, methodName, confirmMessage){
    const btn = container.querySelector("#" + btnId);
    if(!btn) return;
    btn.addEventListener("click", async () => {
      if(confirmMessage && !window.confirm(confirmMessage)) return;
      btn.disabled = true;
      const msg = container.querySelector("#liveSessionMsg");
      const currentSession = await ctx.repository.loadLiveSession(auctionId);
      const expectedRevision = currentSession ? currentSession.revision : 0;
      // Round alpha.6 P0.2-A (§4/CORREZIONE B): invalidazione SINCRONA, PRIMA dell'await della RPC —
      // una risposta poll con epoch precedente viene scartata anche se arriva mentre questa RPC è
      // ancora in corso.
      if(_activeLiveController) _activeLiveController.invalidateForMutation();
      const result = await ctx.repository[methodName](auctionId, expectedRevision);
      if(!result.ok){
        btn.disabled = false;
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Operazione non riuscita.") + "</p>" +
          (result.conflict ? '<p class="hint">Un\'altra scheda ha già modificato la sessione: ricarica la pagina.</p>' : "");
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  }
  runLifecycleCommand("startLiveBtn", "startLiveSession");
  runLifecycleCommand("pauseLiveBtn", "pauseLiveSession");
  runLifecycleCommand("resumeLiveBtn", "resumeLiveSession");
  runLifecycleCommand("closeLiveBtn", "closeLiveSession", "Vuoi davvero chiudere la sessione live? La sessione terminerà e non potrà essere riaperta.");
}

// ---------------------------------------------------------------------
// Round alpha.5-r4-r1: regole live — owner-only, editabile SOLO in 'setup' (parità con
// configure_live_rules(), 0014). Il member non vede questo form: le regole gli sono comunque
// mostrate implicitamente tramite prezzo di apertura/offerta corrente nella sezione nomina.
// ---------------------------------------------------------------------
function renderLiveRulesSection(summary, liveRules, lifecycle){
  if(summary.myRole !== "owner" || lifecycle !== "setup") return "";
  const r = liveRules || { openingPrice:"", initialTimerSeconds:"", resetOnBid:true, resetTimerSeconds:"" };
  return (
    '<div class="card">' +
      "<h3>Regole live</h3>" +
      '<p class="hint">Obbligatorie prima di avviare la sessione live.</p>' +
      '<form id="liveRulesForm">' +
        '<label class="field">Prezzo di apertura' +
          '<input type="number" id="ruleOpeningPrice" min="1" step="1" required value="' + escapeHtml(r.openingPrice===""?"":String(r.openingPrice)) + '"></label>' +
        '<label class="field">Timer iniziale (secondi)' +
          '<input type="number" id="ruleInitialTimer" min="1" step="1" required value="' + escapeHtml(r.initialTimerSeconds===""?"":String(r.initialTimerSeconds)) + '"></label>' +
        '<label class="field"><input type="checkbox" id="ruleResetOnBid"' + (r.resetOnBid?" checked":"") + '> Rinnova il timer a ogni offerta</label>' +
        '<label class="field">Timer di rinnovo (secondi)' +
          '<input type="number" id="ruleResetTimer" min="1" step="1" required value="' + escapeHtml(r.resetTimerSeconds===""?"":String(r.resetTimerSeconds)) + '"></label>' +
        '<button type="submit" class="btn primary sm">' + (liveRules?"Aggiorna regole":"Salva regole") + '</button>' +
      '</form>' +
      '<div id="liveRulesMsg"></div>' +
    "</div>"
  );
}

function wireLiveRulesSection(container, ctx, auctionId, liveRules){
  // Round alpha.6 P0.2-A1 (CORREZIONE R) — vedi wireLobbySection() sopra per la motivazione completa.
  const myGeneration = _renderGeneration;
  const form = container.querySelector("#liveRulesForm");
  if(!form) return;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = container.querySelector("#liveRulesMsg");
    const rules = {
      openingPrice: Number(container.querySelector("#ruleOpeningPrice").value),
      initialTimerSeconds: Number(container.querySelector("#ruleInitialTimer").value),
      resetOnBid: container.querySelector("#ruleResetOnBid").checked,
      resetTimerSeconds: Number(container.querySelector("#ruleResetTimer").value)
    };
    const expectedRevision = liveRules ? liveRules.revision : 0;
    const result = await ctx.repository.configureLiveRules(auctionId, rules, expectedRevision);
    if(!result.ok){
      if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile salvare le regole.") + "</p>" +
        (result.conflict ? '<p class="hint">Un\'altra scheda ha già modificato le regole: ricarica la pagina.</p>' : "");
      return;
    }
    if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
    renderAuctionDetailPage(container, ctx, auctionId);
  });
}

// ---------------------------------------------------------------------
// Round alpha.5-r5-r1: regole di rosa/budget — stesso pattern di renderLiveRulesSection(): solo
// owner, solo in 'setup'. L'unico campo del form è il budget iniziale (50..5000); prezzo
// minimo/slot per ruolo sono SEMPRE i cinque valori fissi scritti dalla RPC (mai un input per essi
// qui) — mostrati in sola lettura sotto il form.
// ---------------------------------------------------------------------
function renderRosterRulesSection(summary, rosterRules, lifecycle){
  if(summary.myRole !== "owner" || lifecycle !== "setup") return "";
  const budgetValue = rosterRules ? String(rosterRules.initialBudgetPerSeat) : "";
  // Round 2.1.0: taglie di rosa per ruolo ora configurabili (prima fisse 3/8/8/6, mai un parametro
  // del chiamante) — vedi migrazione 0020. Precompilate con i valori già salvati, altrimenti con la
  // forma classica (stesso default lato server/adapter, mai un valore diverso qui).
  const slotPor = rosterRules ? String(rosterRules.slotPor) : "3";
  const slotDif = rosterRules ? String(rosterRules.slotDif) : "8";
  const slotCen = rosterRules ? String(rosterRules.slotCen) : "8";
  const slotAtt = rosterRules ? String(rosterRules.slotAtt) : "6";
  return (
    '<div class="card">' +
      "<h3>Regole di rosa e budget</h3>" +
      '<p class="hint">Obbligatorie prima di avviare la sessione live.</p>' +
      '<form id="rosterRulesForm">' +
        '<label class="field">Budget iniziale per squadra (crediti)' +
          '<input type="number" id="ruleInitialBudget" min="50" max="5000" step="1" required value="' + escapeHtml(budgetValue) + '"></label>' +
        '<label class="field">Slot POR' +
          '<input type="number" id="ruleSlotPor" min="0" step="1" required value="' + escapeHtml(slotPor) + '"></label>' +
        '<label class="field">Slot DIF' +
          '<input type="number" id="ruleSlotDif" min="0" step="1" required value="' + escapeHtml(slotDif) + '"></label>' +
        '<label class="field">Slot CEN' +
          '<input type="number" id="ruleSlotCen" min="0" step="1" required value="' + escapeHtml(slotCen) + '"></label>' +
        '<label class="field">Slot ATT' +
          '<input type="number" id="ruleSlotAtt" min="0" step="1" required value="' + escapeHtml(slotAtt) + '"></label>' +
        '<button type="submit" class="btn primary sm">' + (rosterRules?"Aggiorna regole":"Salva regole") + '</button>' +
      '</form>' +
      '<p class="hint">Prezzo minimo: <strong>1</strong> (fisso, non configurabile). La somma degli slot deve essere maggiore di zero e non superiore al budget iniziale.</p>' +
      '<div id="rosterRulesMsg"></div>' +
    "</div>"
  );
}

function wireRosterRulesSection(container, ctx, auctionId, rosterRules){
  // Round alpha.6 P0.2-A1 (CORREZIONE R) — vedi wireLobbySection() sopra per la motivazione completa.
  const myGeneration = _renderGeneration;
  const form = container.querySelector("#rosterRulesForm");
  if(!form) return;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = container.querySelector("#rosterRulesMsg");
    const initialBudgetPerSeat = Number(container.querySelector("#ruleInitialBudget").value);
    const slotPOR = Number(container.querySelector("#ruleSlotPor").value);
    const slotDIF = Number(container.querySelector("#ruleSlotDif").value);
    const slotCEN = Number(container.querySelector("#ruleSlotCen").value);
    const slotATT = Number(container.querySelector("#ruleSlotAtt").value);
    // Round 2.1.0: stessa validazione esplicita lato client di 0020/ArtifactDemoBackendAdapter — mai
    // un clamp silenzioso, sempre un messaggio esplicito prima di inviare alcunché al backend.
    const slotErrors = [];
    if(![slotPOR, slotDIF, slotCEN, slotATT].every(v => Number.isInteger(v) && v >= 0)){
      slotErrors.push("Ciascuna taglia di rosa per ruolo deve essere un intero >= 0.");
    } else {
      const totalSlots = slotPOR + slotDIF + slotCEN + slotATT;
      if(totalSlots <= 0) slotErrors.push("La somma delle taglie di rosa per ruolo deve essere maggiore di zero.");
      else if(Number.isInteger(initialBudgetPerSeat) && totalSlots > initialBudgetPerSeat){
        slotErrors.push("La somma delle taglie di rosa (" + totalSlots + ") non può superare il budget iniziale (" + initialBudgetPerSeat + ").");
      }
    }
    if(slotErrors.length){
      if(msg) msg.innerHTML = slotErrors.map(m => '<p class="errtext">' + escapeHtml(m) + "</p>").join("");
      return;
    }
    const expectedRevision = rosterRules ? rosterRules.revision : 0;
    const result = await ctx.repository.configureRosterRules(auctionId, initialBudgetPerSeat, expectedRevision, slotPOR, slotDIF, slotCEN, slotATT);
    if(!result.ok){
      if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile salvare le regole.") + "</p>" +
        (result.conflict ? '<p class="hint">Un\'altra scheda ha già modificato le regole: ricarica la pagina.</p>' : "");
      return;
    }
    if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
    renderAuctionDetailPage(container, ctx, auctionId);
  });
}

// ---------------------------------------------------------------------
// A7-P1-2 (design D/D2/D3): UNICO punto di calcolo per completezza/anomalia rosa — condiviso fra la
// tabella budget/rosa (stato "active") e il riepilogo di completamento (stati terminali), mai
// duplicato. "Squadra partecipante" = seat con assignedUserId valorizzato, valutato allo stato
// ATTUALE (COMPLETION_SEAT_SET, A7-P1-2 §3) — ESATTAMENTE lo stesso insieme che la guardia SQL in
// migration 0016 usa lato server (BACKEND_UI_COMPLETION_SEAT_SET_IDENTICAL=YES): una seat mai
// assegnata non è mai un partecipante, e zero partecipanti non produce mai "completo" (altrimenti
// un'asta con anche una sola seat orfana non potrebbe MAI raggiungere quello stato).
// ---------------------------------------------------------------------
const ROSTER_ROLES = ["POR","DIF","CEN","ATT"];

// Predicati A7-P1-2D3 §1, tenuti esplicitamente distinti (mai confusi come nella bozza D2 corretta da
// D3): BACKEND_CAPACITY_SATURATED usa >= (fail-safe, stessa direzione della guardia SQL); il
// completamento UI genuino richiede invece uguaglianza ESATTA (==) più nessuna nomina attiva;
// ROSTER_ANOMALY (>) è una classificazione di sola lettura, mai normalizzata qui.
// Esportata per essere testabile in isolamento (stesso principio già in uso in questo file per
// clearNominationCountdown/renderNominationCountdownTick/applyNominationCountdownDeadline/
// applyNonStructuralLiveSnapshot) — mai per un uso al di fuori del modulo pagina.
export function deriveAuctionProductState(seats, rosterRules, purchases, liveSession, lifecycle){
  const participating = seats.filter(s => s.assignedUserId);
  const capacityOf = rosterRules
    ? { POR: rosterRules.slotPor, DIF: rosterRules.slotDif, CEN: rosterRules.slotCen, ATT: rosterRules.slotAtt }
    : null;

  const perSeat = participating.map(seat => {
    const seatPurchases = purchases.filter(p => p.seatId === seat.id);
    const counts = { POR:0, DIF:0, CEN:0, ATT:0 };
    seatPurchases.forEach(p => { if(counts[p.roleAtPurchase] != null) counts[p.roleAtPurchase]++; });
    const roles = capacityOf ? ROSTER_ROLES.map(role => {
      const count = counts[role], capacity = capacityOf[role];
      return { role, count, capacity, saturated: count >= capacity, exact: count === capacity, anomaly: count > capacity };
    }) : [];
    const spent = seatPurchases.reduce((sum,p) => sum + p.amount, 0);
    const remaining = rosterRules ? (rosterRules.initialBudgetPerSeat - spent) : null;
    const rosterCapacity = rosterRules ? (rosterRules.slotPor + rosterRules.slotDif + rosterRules.slotCen + rosterRules.slotAtt) : null;
    return { seat, roles, hasAnomaly: roles.some(r => r.anomaly), spent, remaining, rosterCount: seatPurchases.length, rosterCapacity };
  });

  const hasParticipants = participating.length > 0 && !!capacityOf;
  const backendCapacitySaturated = hasParticipants && perSeat.every(s => s.roles.every(r => r.saturated));
  // uiExactComplete richiede ANCHE nessuna nomina attiva — coerente con COMPLETE_RULE (A7-P1-2D2/D3).
  const uiExactComplete = hasParticipants && perSeat.every(s => s.roles.every(r => r.exact))
    && (!liveSession || liveSession.nominationStatus == null);
  const rosterAnomaly = perSeat.some(s => s.hasAnomaly);

  // A7-P1-2D3 §3: precedenza — (saturato globalmente E anomalia) -> roster-anomaly; altrimenti
  // completo esatto -> complete; altrimenti lifecycle chiuso -> closed-incomplete; altrimenti active.
  // nominationStatus è deliberatamente ESCLUSO dal ramo anomalia: una combinazione corrotta deve
  // sempre finire nello stato di sola lettura più sicuro, mai silenziosamente in "active".
  let state;
  if(backendCapacitySaturated && rosterAnomaly) state = "roster-anomaly";
  else if(uiExactComplete) state = "complete";
  else if(lifecycle === "closed") state = "closed-incomplete";
  else state = "active";

  return { state, perSeat, backendCapacitySaturated, uiExactComplete, rosterAnomaly };
}

// ---------------------------------------------------------------------
// A7-P1-2/A7-P1-3: primitive di rendering condivise fra il riepilogo terminale (complete/closed-
// incomplete/roster-anomaly) e la sezione opponent intelligence in stato "active" — un SOLO calcolo
// di budget/ruoli (perSeat da deriveAuctionProductState()), UNA SOLA forma di markup per la testata
// partecipante e per la tabella rosa, mai due implementazioni indipendenti.
// ---------------------------------------------------------------------
function fmtParticipantDisplayName(seat, membersById){
  const member = membersById.get(seat.assignedUserId);
  return member ? member.displayName : "membro (nome non disponibile)";
}

function renderRoleBadge(roleInfo){
  const cls = roleInfo.anomaly ? "badge danger" : (roleInfo.exact ? "badge ok" : "badge neutral");
  const anomalyAttr = roleInfo.anomaly ? ' data-roster-anomaly-role="' + roleInfo.role + '"' : "";
  return '<span class="' + cls + '"' + anomalyAttr + '>' + roleInfo.role + " " + roleInfo.count + "/" + roleInfo.capacity + "</span>";
}

// A7-P1-3 §10/§6: estratta da renderParticipantSummaryCard() SENZA alterarne l'output — nome/budget/
// residuo/conteggio rosa/badge di ruolo, SEMPRE visibili (mai dietro un toggle, in nessuno dei due
// contesti): questi sono esattamente i campi che il design A7-P1-3D classifica MUST_SHOW nella card
// compatta. Riusata identica dal riepilogo terminale (che la segue sempre con la tabella, mai dietro
// un toggle) e dalla card opponent intelligence in stato active (che la segue con la tabella SOLO
// quando espansa).
function renderParticipantHead(entry, membersById){
  const { seat, roles, spent, remaining, rosterCount, rosterCapacity } = entry;
  const displayName = fmtParticipantDisplayName(seat, membersById);
  return (
    "<h4>" + escapeHtml(displayName) + " · " + escapeHtml(seat.label) + "</h4>" +
    '<p class="hint">Budget iniziale: <strong>' + (remaining==null?"—":(remaining+spent)) + "</strong> · Speso: <strong>" + spent +
      "</strong> · Residuo: <strong>" + (remaining==null?"—":remaining) + "</strong></p>" +
    '<p class="hint">Rosa: <strong>' + rosterCount + "/" + (rosterCapacity==null?"—":rosterCapacity) + '</strong></p>' +
    '<div class="flex gap8 wrap" data-role-breakdown="' + escapeHtml(seat.id) + '">' +
      roles.map(renderRoleBadge).join(" ") +
    "</div>"
  );
}

// A7-P1-3 §10/§7: estratta da renderParticipantSummaryCard() SENZA alterarne l'output — la tabella
// giocatore/ruolo/squadra/prezzo (o il fallback "Nessun giocatore acquistato"), sempre la stessa forma
// nei due contesti. Riusa data-roster-row invariato: mai una seconda vocabolario strutturale per la
// stessa riga di acquisto (A7-P1-3D §20 — deliberatamente NON data-opponent-purchase).
function renderRosterTableFragment(seatId, purchases, catalogPlayers){
  const seatPurchases = purchases.filter(p => p.seatId === seatId).slice().sort((a,b)=>a.purchaseSeq-b.purchaseSeq);
  const rows = seatPurchases.map(p => {
    const player = findCatalogPlayer(catalogPlayers, p.playerFantacalcioId);
    const playerName = player ? player.nome : ("giocatore #" + p.playerFantacalcioId);
    const club = player ? (player.squadra || "—") : "—";
    return '<tr data-roster-row="' + escapeHtml(p.id) + '">' +
      "<td>" + escapeHtml(playerName) + "</td>" +
      "<td>" + escapeHtml(p.roleAtPurchase) + "</td>" +
      "<td>" + escapeHtml(club) + "</td>" +
      "<td>" + p.amount + "</td>" +
    "</tr>";
  }).join("");
  return rows
    ? '<div class="table-wrap"><table><thead><tr><th>Giocatore</th><th>Ruolo</th><th>Squadra</th><th>Prezzo</th></tr></thead><tbody>' + rows + "</tbody></table></div>"
    : '<p class="hint">Nessun giocatore acquistato.</p>';
}

// Riepilogo terminale (complete/closed-incomplete/roster-anomaly) — INVARIATO carattere per carattere
// rispetto a prima del refactor A7-P1-3: stessa identica stringa HTML prodotta, solo composta da due
// funzioni più piccole invece di una sola (regressione esclusa per costruzione, mai per assunzione —
// vedi la suite terminale invariata nei test).
function renderParticipantSummaryCard(entry, membersById, purchases, catalogPlayers){
  const { seat, hasAnomaly } = entry;
  return (
    '<div class="card"' + (hasAnomaly ? ' data-roster-anomaly="true"' : "") +
      ' data-participant-summary="' + escapeHtml(seat.id) + '">' +
      renderParticipantHead(entry, membersById) +
      renderRosterTableFragment(seat.id, purchases, catalogPlayers) +
    "</div>"
  );
}

// ---------------------------------------------------------------------
// A7-P1-3: opponent roster intelligence — sostituisce la vecchia renderBudgetRosterSection()
// (aggregati soltanto, nessun nome giocatore) con una card compatta+espandibile per ogni squadra
// PARTECIPANTE (assigned seat), stesso identico insieme A7_P1_2_COMPLETION_SEAT_SET di A7-P1-2 (mai
// una seconda definizione di "partecipante"). Visibile SOLO in stato di prodotto "active" (mai
// durante gli stati terminali, di competenza esclusiva di renderCompletionSummarySection() — A7-P1-3D
// §13) e mai durante 'setup' (nessun acquisto può ancora esistere).
// ---------------------------------------------------------------------

// A7-P1-3 §7: righe secondarie "ultimo acquisto"/"acquisto più alto" per la SOLA card opponent
// intelligence — calcolate qui, mai aggiunte a renderRosterTableFragment()/renderParticipantSummaryCard()
// (che restano invariate, §10): un cambiamento del riepilogo terminale non è nello scope di questo
// round, indipendentemente da quanto seducente sarebbe riusare lo stesso frammento anche lì.
function renderOpponentSecondaryPurchaseLines(seatId, purchases, catalogPlayers){
  const seatPurchases = purchases.filter(p => p.seatId === seatId);
  if(!seatPurchases.length) return "";
  const latest = seatPurchases.reduce((max,p) => (!max || p.createdAt > max.createdAt) ? p : max, null);
  const highest = seatPurchases.reduce((max,p) => (!max || p.amount > max.amount) ? p : max, null);
  const labelFor = p => {
    const player = findCatalogPlayer(catalogPlayers, p.playerFantacalcioId);
    return player ? player.nome : ("giocatore #" + p.playerFantacalcioId);
  };
  return (
    '<p class="hint">Ultimo acquisto: <strong>' + escapeHtml(labelFor(latest)) + "</strong> (" + latest.amount + " crediti) · " +
      "Acquisto più alto: <strong>" + escapeHtml(labelFor(highest)) + "</strong> (" + highest.amount + " crediti)</p>"
  );
}

// A7-P1-3 §8/§9/§20: card compatta+espandibile di un singolo opponent/own seat. `expanded` decide
// SOLO lo stato iniziale del markup (letto da _expandedOpponentSeatIds al momento del render) — il
// toggle successivo è un puro attributo DOM lato client (wireOpponentIntelligenceSection() sotto),
// mai una nuova chiamata a questa funzione, mai un re-render.
function renderOpponentCard(entry, membersById, purchases, catalogPlayers, isOwnSeat, expanded){
  const { seat, hasAnomaly } = entry;
  const seatId = seat.id;
  const toggleId = "opponentRosterToggle-" + seatId;
  const detailId = "opponentRosterDetail-" + seatId;
  const state = expanded ? "expanded" : "collapsed";
  return (
    '<div class="card opponent-card"' +
      (hasAnomaly ? ' data-roster-anomaly="true"' : "") +
      (isOwnSeat ? ' data-own-seat="true"' : "") +
      ' data-opponent-seat="' + escapeHtml(seatId) + '"' +
      ' data-participant-summary="' + escapeHtml(seatId) + '">' +
      renderParticipantHead(entry, membersById) +
      '<button type="button" class="btn ghost sm opponent-roster-toggle" id="' + escapeHtml(toggleId) + '"' +
        ' data-opponent-roster-toggle="' + escapeHtml(seatId) + '"' +
        ' data-opponent-roster-state="' + state + '"' +
        ' aria-expanded="' + (expanded ? "true" : "false") + '" aria-controls="' + escapeHtml(detailId) + '">' +
        (expanded ? "Nascondi rosa" : "Mostra rosa") +
      "</button>" +
      '<div id="' + escapeHtml(detailId) + '"' + (expanded ? "" : " hidden") + ">" +
        renderOpponentSecondaryPurchaseLines(seatId, purchases, catalogPlayers) +
        renderRosterTableFragment(seatId, purchases, catalogPlayers) +
      "</div>" +
    "</div>"
  );
}

// A7-P1-3 §5: own seat first (se il viewer ne controlla una), poi il resto nell'ordine esistente
// (perSeat eredita l'ordine di seats, già per seat_no) — mai una fabbricazione di una card "propria"
// se il viewer non controlla alcuna seat (VIEWER_WITHOUT_SEAT_SUPPORTED=YES: in quel caso nessun
// riordino, nessuna card marcata, tutte le squadre partecipanti restano "opponent" alla pari).
// Esportata per essere testabile in isolamento (stesso principio già in uso per
// deriveAuctionProductState/clearNominationCountdown/ecc.) — mai per un uso al di fuori del modulo pagina.
export function orderPerSeatOwnFirst(perSeat, myUserId){
  if(myUserId == null) return perSeat;
  const mine = [];
  const others = [];
  for(const entry of perSeat){
    if(entry.seat.assignedUserId === myUserId) mine.push(entry);
    else others.push(entry);
  }
  return mine.concat(others);
}

function renderOpponentIntelligenceSection(perSeat, purchases, catalogPlayers, members, myUserId, liveInteractionAllowed, lifecycle){
  if(!liveInteractionAllowed || lifecycle === "setup" || !perSeat.length) return "";
  const membersById = new Map(members.map(m => [m.userId, m]));
  const ordered = orderPerSeatOwnFirst(perSeat, myUserId);
  const cards = ordered.map(entry => {
    const isOwnSeat = myUserId != null && entry.seat.assignedUserId === myUserId;
    const expanded = _expandedOpponentSeatIds ? _expandedOpponentSeatIds.has(entry.seat.id) : false;
    return renderOpponentCard(entry, membersById, purchases, catalogPlayers, isOwnSeat, expanded);
  }).join("");
  return (
    '<div class="card" data-opponent-intelligence>' +
      "<h3>Squadre in gara</h3>" +
      '<div class="flex gap8 wrap opponent-grid">' + cards + "</div>" +
    "</div>"
  );
}

// A7-P1-3 §8/§9: cablaggio puramente client-side dei toggle — nessuna chiamata al repository,
// nessun re-render strutturale, nessun avvio di polling (TOGGLE_NETWORK_FETCH_DELTA=0/
// TOGGLE_STRUCTURAL_RENDER_DELTA=0 per costruzione: la funzione non contiene alcun `await`). Ogni
// clic aggiorna SOLO _expandedOpponentSeatIds (sopravvive al prossimo render strutturale, §9/§17) e
// gli attributi DOM del bottone/contenitore già presenti nel markup.
function wireOpponentIntelligenceSection(container){
  container.querySelectorAll("[data-opponent-roster-toggle]").forEach(btn => {
    btn.addEventListener("click", () => {
      const seatId = btn.getAttribute("data-opponent-roster-toggle");
      const detail = document.getElementById("opponentRosterDetail-" + seatId);
      if(!detail) return;
      const nowExpanded = detail.hasAttribute("hidden");   // stava collassata -> ora si espande
      if(nowExpanded){
        detail.removeAttribute("hidden");
        if(_expandedOpponentSeatIds) _expandedOpponentSeatIds.add(seatId);
      } else {
        detail.setAttribute("hidden", "");
        if(_expandedOpponentSeatIds) _expandedOpponentSeatIds.delete(seatId);
      }
      btn.setAttribute("aria-expanded", nowExpanded ? "true" : "false");
      btn.setAttribute("data-opponent-roster-state", nowExpanded ? "expanded" : "collapsed");
      btn.textContent = nowExpanded ? "Nascondi rosa" : "Mostra rosa";
    });
  });
}

// =======================================================================
// Round A7-P1-6 — feed attività dell'intera asta (AUCTION_WIDE_ACTIVITY_FEED). Merge PURO e
// deterministico fra auction_nominations e auction_purchases (già esistenti, nessuna nuova tabella/
// RPC/migrazione — design A7-P1-6D §6/§13): mai un log eventi mutabile, mai una persistenza locale
// speculativa, mai Realtime. Ordinamento per revision (openedAtRevision/closedAtRevision/
// resultingRevision — TUTTE derivate dallo stesso contatore auction_live_sessions.revision, mai da
// timestamp, verificato staticamente in A7-P1-6D §2/§7: nessuna ambiguità di ordine possibile). Ogni
// riga di auction_nominations produce SEMPRE un evento nomination_opened; SOLO se il suo stato finale
// persistito è "unsold" produce ANCHE un secondo evento player_unsold dalla STESSA riga — una nomina
// letta DOPO essere già diventata unsold produce quindi entrambi gli eventi in un solo fetch
// (RAPID_UNSOLD_SINGLE_FETCH_RECOVERS_BOTH_EVENTS=YES). Esportata per essere testabile in isolamento,
// indipendentemente dal rendering DOM (stesso motivo di filterCatalogPlayers/orderPerSeatOwnFirst).
export function deriveAuctionActivity(nominations, purchases){
  const events = [];
  (nominations || []).forEach(n => {
    events.push({
      type: "nomination_opened", id: n.id, nominationId: n.id, seatId: n.nominatingSeatId,
      playerFantacalcioId: n.playerFantacalcioId, amount: null,
      orderKey: n.openedAtRevision, timestamp: n.openedAt
    });
    if(n.status === "unsold"){
      events.push({
        type: "player_unsold", id: n.id + "-unsold", nominationId: n.id, seatId: n.nominatingSeatId,
        playerFantacalcioId: n.playerFantacalcioId, amount: null,
        orderKey: n.closedAtRevision, timestamp: n.closedAt
      });
    }
  });
  (purchases || []).forEach(p => {
    events.push({
      type: "player_sold", id: p.id, nominationId: p.nominationId, seatId: p.seatId,
      playerFantacalcioId: p.playerFantacalcioId, amount: p.amount,
      orderKey: p.resultingRevision, timestamp: p.createdAt
    });
  });
  // Un pareggio di orderKey è già provato impossibile per costruzione (contatore unico, incrementato
  // sotto lock — A7-P1-6D §7) — il confronto su `id` qui sotto è puro irrigidimento difensivo, mai
  // osservabile nella pratica, MAI usato come criterio primario (§26: l'ordine non deve mai dipendere
  // da una stringa formattata, e qui non dipende nemmeno da id — solo da orderKey).
  return events.sort((a, b) => (a.orderKey - b.orderKey) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

// Contratto DOM stabile (A7-P1-6D §24) — vocabolario deliberatamente SEPARATO da data-bid-history-*
// (concetti diversi, mai la stessa struttura riusata per due significati). I tre figli fissi
// (messaggio d'errore/messaggio vuoto/lista) esistono SEMPRE, nascosti/mostrati via `hidden` — mai
// un cambio di tipo di elemento fra stati (nessuna patch leggera qui: il CONTENUTO resta di
// competenza esclusiva del render completo, §13 del design D3 — solo l'indicatore di errore/stale
// può essere toccato da applyActivityFeedErrorLightPatch() più sotto in applyNonStructuralLiveSnapshot()).
function renderActivityFeedSection(nominations, purchases, catalogPlayers, seats, hasEverStarted, nominationsError){
  if(!hasEverStarted) return "";
  const state = nominationsError ? "error" : (nominations.length === 0 && purchases.length === 0 ? "empty" : "ready");
  const events = nominationsError ? [] : deriveAuctionActivity(nominations, purchases);
  const rows = events.map(e => renderActivityFeedEntry(e, seats, catalogPlayers)).join("");
  return (
    '<div class="card" data-auction-activity-feed data-auction-activity-state="' + state + '">' +
      "<h3>Attività asta</h3>" +
      '<p class="errtext" data-auction-activity-error-msg' + (nominationsError ? "" : " hidden") + '>Impossibile caricare il feed attività.</p>' +
      '<p class="hint" data-auction-activity-empty-msg' + (state === "empty" ? "" : " hidden") + '>Nessuna nomina ancora per questa asta.</p>' +
      '<ol class="activity-feed-list">' + rows + "</ol>" +
    "</div>"
  );
}

// Identità seat/team (mai un raw UUID, mai un member display name — §25 del design, stessa
// convenzione già usata da A7-P1-5). Nessun timestamp mostrato (§26 del design: DEFER per v1 — solo
// la posizione in lista, mai ambigua, comunica l'ordine).
function renderActivityFeedEntry(event, seats, catalogPlayers){
  const seat = seats.find(s => s.id === event.seatId);
  const seatLabel = seat ? seat.label : "—";
  const player = findCatalogPlayer(catalogPlayers, event.playerFantacalcioId);
  const playerLabel = player ? player.nome : ("giocatore #" + event.playerFantacalcioId);
  let text;
  if(event.type === "nomination_opened") text = escapeHtml(playerLabel) + " nominato da <strong>" + escapeHtml(seatLabel) + "</strong>";
  else if(event.type === "player_sold") text = escapeHtml(playerLabel) + " venduto a <strong>" + escapeHtml(seatLabel) + "</strong> per <strong>" + event.amount + " crediti</strong>";
  else if(event.type === "player_unsold") text = escapeHtml(playerLabel) + " invenduto";
  else text = "";
  return (
    '<li data-auction-activity-event="' + escapeHtml(String(event.id)) + '" data-auction-activity-event-type="' + escapeHtml(event.type) + '" data-auction-activity-nomination="' + escapeHtml(String(event.nominationId)) + '" data-auction-activity-seat="' + escapeHtml(String(event.seatId)) + '">' +
      text +
    "</li>"
  );
}

function renderCompletionSummarySection(summary, seats, members, rosterRules, purchases, catalogPlayers, liveSession, derivedState, hasSummaryReadError){
  if(!hasSummaryReadError && derivedState.state === "active") return "";

  if(hasSummaryReadError){
    // A7-P1-9: unica azione aggiunta a questo ramo — un pulsante di retry che richiama per intero
    // renderAuctionDetailPage() (wireCompletionSummarySection() più sotto), riusando lo stesso ciclo
    // dispose-first + render-generation già usato da ogni altro handler di mutazione di questo file
    // (A7-P1-9D §2/§4). Mai una nuova lettura parziale, mai un nuovo store di stato: il click non è
    // mai trattato come prova che una lettura sia riuscita — l'errore sparisce SOLO se il prossimo
    // render, con letture realmente fresche, non produce più hasSummaryReadError=true.
    return (
      '<div class="card" data-completion-summary data-completion-summary-state="error">' +
        "<h3>Riepilogo asta</h3>" +
        '<p class="errtext">Impossibile caricare il riepilogo completo (rosa, budget o acquisti). Riprova più tardi.</p>' +
        '<button type="button" class="btn ghost sm" data-completion-summary-retry>Riprova</button>' +
      "</div>"
    );
  }

  const { state, perSeat } = derivedState;
  const membersById = new Map(members.map(m => [m.userId, m]));
  const totalSpend = purchases.reduce((sum,p)=>sum+p.amount, 0);
  const totalRemaining = perSeat.reduce((sum,s)=>sum+(s.remaining||0), 0);
  const highest = purchases.reduce((max,p) => (!max || p.amount > max.amount) ? p : max, null);
  const highestSeat = highest ? seats.find(s => s.id === highest.seatId) : null;
  const highestPlayer = highest ? findCatalogPlayer(catalogPlayers, highest.playerFantacalcioId) : null;
  const lastPurchase = purchases.reduce((max,p) => (!max || p.createdAt > max.createdAt) ? p : max, null);
  const closedAtLine = liveSession && liveSession.closedAt ? ("Chiusa il: " + escapeHtml(fmtDate(liveSession.closedAt)) + ".") : "";

  const heroByState = {
    "complete": {
      badgeClass: "ok", title: "Asta conclusa",
      sub: (closedAtLine || (lastPurchase ? ("Ultimo acquisto: " + escapeHtml(fmtDate(lastPurchase.createdAt)) + " (non è un istante di completamento persistito).") : ""))
    },
    "closed-incomplete": {
      badgeClass: "warn", title: "Sessione chiusa",
      sub: "Rose incomplete." + (closedAtLine ? " " + closedAtLine : "")
    },
    "roster-anomaly": {
      badgeClass: "danger", title: "Dati rosa anomali",
      sub: "Rilevato un conteggio superiore alla capacità configurata in almeno una rosa — nessuna normalizzazione automatica."
    }
  }[state];

  return (
    '<div class="card complete-hero' + (state==="roster-anomaly"?" danger":"") + '" data-completion-summary data-completion-summary-state="ready">' +
      '<span class="badge ' + heroByState.badgeClass + '">' + escapeHtml(heroByState.title) + "</span>" +
      "<h2>" + escapeHtml(summary.name) + "</h2>" +
      (heroByState.sub ? '<p class="hint">' + heroByState.sub + "</p>" : "") +
      '<p class="hint">Partecipanti: <strong>' + perSeat.length + '</strong> · Acquisti totali: <strong>' + purchases.length +
        '</strong> · Spesa totale: <strong>' + totalSpend + '</strong> · Residuo totale: <strong>' + totalRemaining + " crediti</strong></p>" +
      (highest
        ? '<p class="hint">Acquisto più alto: <strong>' + escapeHtml(highestPlayer?highestPlayer.nome:("giocatore #"+highest.playerFantacalcioId)) +
          "</strong> per <strong>" + highest.amount + " crediti</strong> (" + escapeHtml(highestSeat?highestSeat.label:"—") + ")</p>"
        : ""
      ) +
    "</div>" +
    perSeat.map(entry => renderParticipantSummaryCard(entry, membersById, purchases, catalogPlayers)).join("")
  );
}

// A7-P1-9: unico handler aggiunto — richiama per intero renderAuctionDetailPage() tramite l'helper
// condiviso (round Beta-P3-7) riusando lo stesso ciclo dispose-first + render-generation già
// garantito da quella funzione (mai una nuova lettura parziale/store di stato qui). Un secondo
// click mentre il primo è ancora in corso richiama di nuovo disposeActiveAuctionDetailResources(),
// che incrementa subito _renderGeneration: la continuazione asincrona del primo click si ritrova
// quindi con una generation ormai stale e si ferma da sola prima di scrivere DOM/montare un poller —
// AT_MOST_ONE_CURRENT_RENDER_CAN_COMPLETE per costruzione, non per il solo `disabled` (che resta
// comunque una comodità UX: evita fetch sprecati, mai la garanzia di correttezza).
function wireCompletionSummarySection(container, ctx, auctionId){
  const retryBtn = container.querySelector("[data-completion-summary-retry]");
  if(!retryBtn) return;
  retryBtn.addEventListener("click", async () => {
    retryBtn.disabled = true;
    await attemptAuctionDetailReload(container, ctx, auctionId, "completion-summary-retry")();
  });
}

// ---------------------------------------------------------------------
// Round alpha.5-r4-r1: nomina/offerta/timer — visibile solo con lifecycle='running'. Il countdown è
// SOLO display (nessuna scrittura server al secondo, nessun Realtime): calcolato lato client dal
// `deadlineAt` server-authoritative già ottenuto, mai un timer inventato lato client. Il finalize non è
// mai automatico in questo round: alla scadenza compare un pulsante "Finalizza" (owner o member).
// ---------------------------------------------------------------------
function findCatalogPlayer(catalogPlayers, fantacalcioId){
  return catalogPlayers.find(p => p.fantacalcioId === fantacalcioId) || null;
}

// ---------------------------------------------------------------------
// Round alpha.7 (A7-P1-1): pannello statistiche giocatore — candidato in anteprima (§12) E nomina
// attiva (§13). Identità già risolta SENZA alcun matching per nome (§11): fantacalcioId ->
// catalogPlayers (già in memoria dal cold-render) -> player.id (uuid) -> getPlayerSeasonStats().
// Cache module-locale deduplicata per Promise in-flight (A7-P1-1D2 §10): una seconda richiesta
// concorrente per la STESSA chiave fantacalcioId:seasonKey riceve la stessa Promise, mai una
// seconda chiamata al repository. Un fallimento non viene MAI messo in cache di successo (§11): la
// entry viene rimossa da _statsCache PRIMA di ripropagare l'errore — ma la chiave resta marcata in
// _statsErrorKeys (round A7-P1-1A §4/§6) finché QUESTA stessa funzione non viene richiamata di
// nuovo per quella chiave, cosa che avviene SOLO per un'interazione esplicita (onchange
// dell'anteprima) o per una NUOVA nomina/candidato — mai per un semplice render strutturale
// automatico, che consulta il marker PRIMA di chiamare questa funzione (vedi il trigger della
// nomina attiva più sotto) invece di ritentare da sé. Ogni chiamata a questa funzione — quindi ogni
// tentativo genuino, automatico o esplicito che sia — cancella comunque il marker per la sua stessa
// chiave PRIMA di partire: è la richiesta stessa, non il chiamante, a decidere che si sta
// ritentando. Mai chiamata da applyNonStructuralLiveSnapshot()/dal poll (§13/§20): solo dal
// cold-render (nomina attiva) e dall'onchange del candidato (anteprima).
function fetchPlayerStatsCached(ctx, catalogPlayers, fantacalcioId, seasonKey){
  if(!seasonKey || fantacalcioId == null) return Promise.resolve(null);
  const key = fantacalcioId + ":" + seasonKey;
  if(_statsCache.has(key)) return _statsCache.get(key);
  const catalogPlayer = findCatalogPlayer(catalogPlayers, fantacalcioId);
  if(!catalogPlayer) return Promise.resolve(null);
  _statsErrorKeys.delete(key);
  const promise = ctx.catalogRepository.getPlayerSeasonStats(catalogPlayer.id, seasonKey)
    .catch(err => {
      _statsCache.delete(key);
      _statsErrorKeys.add(key);
      throw err;
    });
  _statsCache.set(key, promise);
  return promise;
}

// Round A7-P1-1A §4: usata SOLO dal trigger automatico (nomina attiva, ad ogni render strutturale)
// per decidere se un tentativo sia appropriato — mai dal percorso esplicito (onchange
// dell'anteprima), che tenta sempre e comunque via fetchPlayerStatsCached() sopra.
function hasKnownStatsError(fantacalcioId, seasonKey){
  if(!seasonKey || fantacalcioId == null) return false;
  return _statsErrorKeys.has(fantacalcioId + ":" + seasonKey);
}

// Stati strutturali del pannello — MAI riusato data-catalog-state (contratto proprio di
// CatalogPage.js, round A7-P0-2): questo pannello ha il proprio, scoped al pannello stesso, e un
// suo fallimento non deve mai bloccare nomina/rilancio (§14, STATS_FAILURE_BLOCKS_*=NO).
function renderStatsPanelMarkup(panelId, state, stats){
  let body = "";
  if(state === "loading") body = '<p class="hint">Caricamento statistiche…</p>';
  else if(state === "error") body = '<p class="hint">Statistiche non disponibili al momento.</p>';
  else if(state === "empty") body = '<p class="hint">Nessuna statistica disponibile per questo giocatore.</p>';
  else if(state === "ready" && stats){
    body =
      '<ul class="player-stats-list">' +
        "<li>Presenze: <strong>" + (stats.presenze==null?"—":stats.presenze) + "</strong></li>" +
        "<li>Media voto: <strong>" + (stats.mv==null?"—":stats.mv) + "</strong></li>" +
        "<li>Fantamedia: <strong>" + (stats.fm==null?"—":stats.fm) + "</strong></li>" +
        "<li>Gol: <strong>" + (stats.gf==null?"—":stats.gf) + "</strong></li>" +
        "<li>Assist: <strong>" + (stats.assist==null?"—":stats.assist) + "</strong></li>" +
      "</ul>";
  }
  return '<div class="player-stats-panel" id="' + panelId + '" data-player-stats-state="' + state + '">' + body + "</div>";
}

function repaintStatsPanel(container, panelId, state, stats){
  const panel = container.querySelector("#" + panelId);
  if(!panel) return;
  panel.outerHTML = renderStatsPanelMarkup(panelId, state, stats);
}

// =======================================================================
// Round A7-P1-7 — picker di nomina con ricerca testuale + esclusione dei giocatori già venduti
// (design A7-P1-7D/D2, accettato). Modello puro, UNICA fonte di verità per tre consumatori
// diversi — il render iniziale, il rifiltro locale ad ogni tasto (wireNominationSection), e il
// controllo di coerenza al submit — mai una seconda implementazione divergente (§6/§12 del
// design D2). Esclusione SOLO da auction_purchases (mai da auction_nominations): un giocatore
// nominato e poi invenduto resta eleggibile ogni volta (§3/§13 del design D).
//
// Precedenza degli stati (§3 del design D2, affinata in implementazione — CATALOG_READ_FAILURE
// MUST NOT BE REPRESENTED AS CATALOG_EMPTY):
//   catalogPlayersError=true            -> "catalog-unavailable" (mai "catalog-empty": un fetch
//                                          fallito non deve mai essere presentato come "il
//                                          catalogo esiste ed è vuoto" — sono fatti diversi)
//   catalogPlayers.length === 0         -> "catalog-empty" (lettura riuscita, zero righe reali)
//   purchasesError=true                 -> "purchases-unknown" — l'esclusione venduti è SALTATA
//                                          (mai [] trattato come "zero venduti confermati", il
//                                          difetto esatto che questo round previene), la ricerca
//                                          continua a filtrare il catalogo grezzo, il backend
//                                          nominate_player() resta l'unica autorità reale
//   (nessun errore)                     -> eligible = catalogPlayers - venduti; "no-eligible" se
//                                          vuoto, altrimenti filtro testuale; "search-empty" se
//                                          una query non vuota non produce alcun risultato,
//                                          altrimenti "ready"
export function computeNominationCandidateModel(catalogPlayers, catalogPlayersError, purchases, purchasesError, searchQuery){
  if(catalogPlayersError) return { state: "catalog-unavailable", players: [] };
  if(!catalogPlayers.length) return { state: "catalog-empty", players: [] };
  if(purchasesError){
    return { state: "purchases-unknown", players: sortNominationCandidates(filterCatalogPlayers(catalogPlayers, { search: searchQuery })) };
  }
  const soldFantacalcioIds = new Set(purchases.map(p => p.playerFantacalcioId));
  const eligible = catalogPlayers.filter(p => !soldFantacalcioIds.has(p.fantacalcioId));
  if(!eligible.length) return { state: "no-eligible", players: [] };
  const filtered = filterCatalogPlayers(eligible, { search: searchQuery });
  const normalizedQuery = (searchQuery || "").trim();
  if(normalizedQuery !== "" && filtered.length === 0) return { state: "search-empty", players: [] };
  return { state: "ready", players: sortNominationCandidates(filtered) };
}

// Ordine deterministico preesistente (ruolo poi nome, stesso localeCompare di sempre) — mai
// sostituito dall'ordine di inserimento nell'array dopo esclusione/filtro (§10 del design D2).
function sortNominationCandidates(players){
  return players.slice().sort((a,b) => (a.ruolo||"").localeCompare(b.ruolo||"") || (a.nome||"").localeCompare(b.nome||""));
}

// Stati in cui il picker non ha alcuna azione utile possibile (mai per "search-empty"/
// "purchases-unknown", entrambi transitori/degradati ma pienamente utilizzabili — §4/§9 D2).
function nominationPickerIsDisabled(state){
  return state === "no-eligible" || state === "catalog-empty" || state === "catalog-unavailable";
}

const NOMINATION_PICKER_STATE_MESSAGES = {
  "search-empty": "Nessun giocatore trovato per questa ricerca.",
  "no-eligible": "Nessun giocatore disponibile: tutti i giocatori del catalogo sono già stati acquistati in questa asta.",
  "catalog-empty": "Il catalogo corrente non contiene giocatori.",
  "catalog-unavailable": "Impossibile caricare il catalogo giocatori. Riprova più tardi.",
  "purchases-unknown": "Non è stato possibile verificare i giocatori già acquistati: la lista potrebbe includere giocatori non più disponibili."
};

function renderNominationPlayerOptionsMarkup(players){
  return players.map(p => '<option value="' + p.fantacalcioId + '">' + escapeHtml((p.ruolo||"—") + " · " + p.nome + " (" + (p.squadra||"—") + ")") + "</option>").join("");
}

// Contratto DOM stabile (§4/§10 del design D2) — vocabolario dedicato, mai riusato da bid-history/
// activity-feed. `data-nomination-player-state` è il segnale macchina-leggibile primario: i test
// non devono mai dedurre lo stato semantico dal solo testo italiano del messaggio.
function renderNominationPickerMarkup(model, searchQuery){
  const disabled = nominationPickerIsDisabled(model.state);
  const messageText = NOMINATION_PICKER_STATE_MESSAGES[model.state] || "";
  return (
    '<div data-nomination-player-picker data-nomination-player-state="' + model.state + '">' +
      '<label class="field" for="nominatePlayerSearch">Cerca giocatore' +
        '<input type="text" id="nominatePlayerSearch" data-nomination-player-search placeholder="Nome o squadra…" value="' + escapeHtml(searchQuery||"") + '"' + (disabled?" disabled":"") + '>' +
      "</label>" +
      '<select id="nominatePlayerSelect"' + (disabled?" disabled":"") + '><option value="">seleziona giocatore…</option>' + renderNominationPlayerOptionsMarkup(model.players) + "</select>" +
      '<p class="hint" data-nomination-player-message' + (model.state === "ready" ? " hidden" : "") + '>' + escapeHtml(messageText) + "</p>" +
    "</div>"
  );
}

function renderNominationSection(summary, seats, liveSession, liveRules, catalogPlayers, catalogPlayersError, purchases, purchasesError, myUserId, justFinalizedOutcome, justSettledInfo, liveInteractionAllowed, bidHistory, bidHistoryError){
  // A7-P1-2 §13/§23: nessun controllo di nomina/offerta/finalizzazione a meno che lo stato di prodotto
  // sia "active" E nessuna lettura di riepilogo sia fallita (A7-P1-2A2 §2 — liveInteractionAllowed, mai
  // il solo stato derivato) — indipendentemente dal lifecycle grezzo (uno stato "complete"/"roster-anomaly"
  // può verificarsi anche con lifecycle ancora 'running'/'paused', A7-P1-2D3 §2/§3: il completamento è
  // un segnale derivato, mai legato solo a lifecycle='closed'). Il riepilogo di completamento
  // (renderCompletionSummarySection) sostituisce interamente questa sezione per quegli stati.
  if(!liveInteractionAllowed) return "";
  if(!liveSession || liveSession.lifecycle !== "running") return "";
  const isOwner = summary.myRole === "owner";
  const mySeat = seats.find(s => s.assignedUserId === myUserId) || null;
  const status = liveSession.nominationStatus;

  // Round alpha.5-r5-r1: conferma "Acquistato" one-shot, mostrata SOLO nel render immediatamente
  // successivo a una chiamata a settleNomination() riuscita da QUESTA scheda — mai persistita (parità
  // col trattamento di justFinalizedOutcome="unsold" sopra).
  const settledNotice = justSettledInfo
    ? (() => {
        const player = findCatalogPlayer(catalogPlayers, justSettledInfo.playerFantacalcioId);
        const seat = seats.find(s => s.id === justSettledInfo.seatId);
        const playerLabel = player ? player.nome : ("giocatore #" + justSettledInfo.playerFantacalcioId);
        return '<p class="okmsg" id="settledNotice">Acquistato: <strong>' + escapeHtml(playerLabel) + "</strong> per <strong>" +
          justSettledInfo.amount + " crediti</strong> da <strong>" + escapeHtml(seat?seat.label:"—") + "</strong>.</p>";
      })()
    : "";

  if(!status){
    // CORREZIONE round alpha.5-r4-r2 (BLOCKER 5, audit indipendente): messaggio distinto e
    // inequivocabile per l'esito "unsold" appena ottenuto da QUESTA chiamata a finalizeIfExpired() —
    // mai confuso con "nessuna nomina è mai stata aperta". Lo stato è già libero per una nuova
    // nomina (nominationStatus è già tornato null): il form sottostante lo dimostra restando
    // immediatamente disponibile. Mai persistito: un refresh/navigazione successivi non lo
    // ripropongono.
    const unsoldNotice = justFinalizedOutcome === "unsold"
      ? '<p class="notice" id="unsoldNotice">Nomina conclusa: giocatore invenduto.</p>' : "";
    // Nessuna nomina in corso: solo l'owner può nominare. Round alpha.5-r5-r1: dopo un settlement
    // riuscito nomination_status torna a null esattamente come "unsold" — settledNotice compare qui,
    // mai in pending_settlement (quello stato non esiste più a questo punto).
    if(!isOwner) return '<div class="card"><h3>Nomina</h3>' + settledNotice + unsoldNotice + '<p class="hint">In attesa che il proprietario nomini un giocatore.</p></div>';
    // A7-P1-7: query di ricerca SEMPRE vuota al render iniziale/strutturale — mai preservata da un
    // render precedente (§16/§19 del design: nessuna infrastruttura di stato persistente per questo
    // form, esattamente come ogni altro campo di input di questa pagina attraverso un render
    // strutturale).
    const nominationModel = computeNominationCandidateModel(catalogPlayers, catalogPlayersError, purchases, purchasesError, "");
    const nominationDisabled = nominationPickerIsDisabled(nominationModel.state);
    return (
      '<div class="card">' +
        "<h3>Nomina</h3>" +
        settledNotice +
        unsoldNotice +
        (liveRules
          ? (
            '<form id="nominateForm" class="flex gap8 wrap">' +
              '<select id="nominateSeat">' + seats.map(s => '<option value="' + escapeHtml(s.id) + '">' + escapeHtml(s.label) + "</option>").join("") + "</select>" +
              renderNominationPickerMarkup(nominationModel, "") +
              '<button type="submit" class="btn primary sm" id="nominateSubmitBtn"' + (nominationDisabled?" disabled":"") + '>Nomina</button>' +
            "</form>" +
            // Round alpha.7 (A7-P1-1 §12): anteprima statistiche del candidato selezionato nel
            // <select> qui sopra — idle finché nessuna selezione, mai un fetch sul cold-render stesso.
            renderStatsPanelMarkup("candidatePreviewStatsPanel", "idle", null)
          )
          : '<p class="hint">Regole live non configurate.</p>'
        ) +
        '<div id="nominateMsg"></div>' +
      "</div>"
    );
  }

  // Nomina open o pending_settlement: dati sempre presenti per invariante (nomination_consistency).
  const player = findCatalogPlayer(catalogPlayers, liveSession.nominatedPlayerFantacalcioId);
  // CORREZIONE round alpha.5-r4-r2 (BLOCKER 5, audit indipendente): il ruolo mostrato qui sarebbe
  // quello CORRENTE del catalogo, non quello congelato alla nomina (playerRoleAtNomination, mai letto
  // da questa pagina) — mostrarlo nella card live lo farebbe passare implicitamente per il ruolo della
  // nomina. Soluzione minima approvata per questo round: nome/squadra senza ruolo, nessuna nuova API
  // di snapshot del ruolo.
  const playerLabel = player ? (player.nome + " (" + (player.squadra||"—") + ")") : ("giocatore #" + liveSession.nominatedPlayerFantacalcioId);
  const nominatingSeat = seats.find(s => s.id === liveSession.nominatingSeatId);
  const highestSeat = liveSession.highestBidderSeatId ? seats.find(s => s.id === liveSession.highestBidderSeatId) : null;

  if(status === "pending_settlement"){
    // Round alpha.5-r5-r1: il dead-end r4 è sostituito da un'azione reale ("Finalizza acquisto"),
    // coerente con la server authority di settle_nomination() — owner O current member, MAI ristretto
    // silenziosamente alla sola winning seat (chiunque appartenga all'asta può finalizzare).
    return (
      '<div class="card">' +
        "<h3>Nomina</h3>" +
        "<p><strong>" + escapeHtml(playerLabel) + "</strong></p>" +
        "<p>Offerta vincente provvisoria: <strong>" + escapeHtml(highestSeat?highestSeat.label:"—") +
          "</strong>, <strong>" + liveSession.currentHighestBid + " crediti</strong>.</p>" +
        '<p class="notice">In attesa di finalizzazione — giocatore non ancora acquistato.</p>' +
        '<button class="btn primary sm" id="settleNominationBtn">Finalizza acquisto</button>' +
        '<div id="settleNominationMsg"></div>' +
        // Round A7-P1-5: storico offerte della nomina corrente — secondario, sotto lo stato/azione,
        // sopra le statistiche (§6/§13 del design).
        renderBidHistorySection(liveSession.currentNominationId, seats, bidHistory, bidHistoryError) +
        // Round alpha.7 (A7-P1-1 §13): stesso pannello statistiche della nomina attiva, popolato dal
        // cold-render (mai dal poll) subito dopo questo innerHTML — vedi renderAuctionDetailPage().
        renderStatsPanelMarkup("nominationStatsPanel", "loading", null) +
      "</div>"
    );
  }

  // status === "open" — gerarchia r5: player -> timer -> current bid -> highest bidder -> bid action
  // -> opponent intelligence (quest'ultima in renderOpponentIntelligenceSection(), appesa subito dopo questa card).
  const canBid = mySeat && liveSession.highestBidderSeatId !== mySeat.id;
  return (
    '<div class="card">' +
      "<h3>Nomina in corso</h3>" +
      "<p><strong>" + escapeHtml(playerLabel) + "</strong> · nominato da <strong>" + escapeHtml(nominatingSeat?nominatingSeat.label:"—") + "</strong></p>" +
      "<p>Prezzo di apertura: <strong>" + (liveRules ? liveRules.openingPrice : "—") + " crediti</strong></p>" +
      '<p class="mono" id="nominationCountdown" data-deadline="' + escapeHtml(liveSession.deadlineAt||"") + '">…</p>' +
      '<p class="hint">Offerta attuale: <strong id="currentHighestBidText">' + (liveSession.currentHighestBid==null ? "nessuna" : (liveSession.currentHighestBid + " crediti")) + "</strong></p>" +
      '<p class="hint">Miglior offerente: <strong id="highestBidderText">' + (highestSeat ? escapeHtml(highestSeat.label) : "—") + "</strong></p>" +
      (canBid
        ? '<form id="bidForm" class="flex gap8 wrap">' +
            '<input type="number" id="bidAmount" min="1" step="1" placeholder="Importo" required>' +
            '<button type="submit" class="btn primary sm">Offri</button>' +
          "</form>"
        : (mySeat ? '<p class="hint">Sei già tu il miglior offerente.</p>' : "")
      ) +
      '<div id="bidMsg"></div>' +
      '<button class="btn ghost sm" id="finalizeNominationBtn" disabled>Finalizza (nomina non ancora scaduta)</button>' +
      '<div id="finalizeNominationMsg"></div>' +
      // Round A7-P1-5: storico offerte della nomina corrente — secondario, sotto il form/lo stato di
      // offerta, sopra le statistiche (§6/§13 del design). Aggiornato in modo leggero dal poll per un
      // bid altrui (applyBidHistoryLightPatch, mai una ricostruzione di questa card).
      renderBidHistorySection(liveSession.currentNominationId, seats, bidHistory, bidHistoryError) +
      // Round alpha.7 (A7-P1-1 §13): pannello statistiche della nomina attiva — popolato dal
      // cold-render subito dopo questo innerHTML, MAI da applyNonStructuralLiveSnapshot() (§13/§20).
      renderStatsPanelMarkup("nominationStatsPanel", "loading", null) +
    "</div>"
  );
}

// Round A7-P1-5 — storico offerte della SOLA nomina corrente (§4 del design: CURRENT_NOMINATION_BID_
// HISTORY, mai l'activity feed auction-wide, esplicitamente OUT_OF_SCOPE/OPEN in questo round).
// Contratto strutturale stabile (§8/§32): data-bid-history-state/-nomination/-entry/-seat/-seq, mai
// dedotto da copia italiana. I tre figli (messaggio d'errore/messaggio vuoto/lista) esistono SEMPRE,
// nascosti/mostrati via `hidden` — mai un cambio di tipo di elemento fra stati, cosicché
// applyBidHistoryLightPatch() (poll) possa aggiornare la STESSA struttura senza mai ricostruirla.
function renderBidHistorySection(nominationId, seats, bidHistory, bidHistoryError){
  const isEmpty = !bidHistoryError && (!Array.isArray(bidHistory) || bidHistory.length === 0);
  const state = bidHistoryError ? "error" : (isEmpty ? "empty" : "ready");
  const rows = bidHistoryError || !Array.isArray(bidHistory) ? "" : bidHistory.map(b => renderBidHistoryEntry(b, seats)).join("");
  return (
    '<div data-bid-history data-bid-history-state="' + state + '" data-bid-history-nomination="' + escapeHtml(String(nominationId)) + '">' +
      "<h3>Storico offerte</h3>" +
      '<p class="errtext" data-bid-history-error-msg' + (bidHistoryError ? "" : " hidden") + '>Impossibile caricare lo storico offerte.</p>' +
      '<p class="hint" data-bid-history-empty-msg' + (isEmpty ? "" : " hidden") + '>Nessuna offerta ancora per questa nomina.</p>' +
      '<ol class="bid-history-list">' + rows + "</ol>" +
    "</div>"
  );
}
// Seat/team come identità (§25 del design, mai un member display name/user id grezzo — stessa
// convenzione già usata per nominatingSeat/highestSeat sopra). Nessun timestamp questo round (§27).
function renderBidHistoryEntry(bid, seats){
  const seat = seats.find(s => s.id === bid.seatId);
  const label = seat ? seat.label : "—";
  return (
    '<li data-bid-history-entry="' + escapeHtml(String(bid.id)) + '" data-bid-history-seat="' + escapeHtml(String(bid.seatId)) + '" data-bid-history-seq="' + bid.bidSeq + '">' +
      escapeHtml(label) + " — <strong>" + bid.amount + " crediti</strong>" +
    "</li>"
  );
}

function wireNominationSection(container, ctx, auctionId, seats, liveSession, myUserId, currentLiveSnapshotRef, catalogPlayers, catalogPlayersError, purchases, purchasesError){
  // Round alpha.6 P0.2-A1 (CORREZIONE R) — vedi wireLobbySection() sopra per la motivazione completa.
  const myGeneration = _renderGeneration;

  // A7-P1-7 (§5/§11 del design D2) — filtro puramente client-side: patch LOCALE delle sole
  // <option> del picker, mai un container.innerHTML/renderAuctionDetailPage() ad ogni tasto (che
  // distruggerebbe/ricreerebbe countdown/riferimenti DOM del poll mentre l'utente sta scrivendo).
  // Invariante di selezione ESPLICITA (§11 D2, mai implicita): l'id precedentemente selezionato
  // viene verificato contro il nuovo elenco PRIMA di decidere se conservarlo, mai assunto dal solo
  // comportamento nativo di rimozione di un'<option>.
  const nominatePlayerSearchInput = container.querySelector("#nominatePlayerSearch");
  if(nominatePlayerSearchInput){
    nominatePlayerSearchInput.addEventListener("input", () => {
      const pickerEl = container.querySelector("[data-nomination-player-picker]");
      const selectEl = container.querySelector("#nominatePlayerSelect");
      const nominateBtn = container.querySelector("#nominateSubmitBtn");
      if(!pickerEl || !selectEl) return;
      const previousSelectedValue = selectEl.value;
      const model = computeNominationCandidateModel(catalogPlayers, catalogPlayersError, purchases, purchasesError, nominatePlayerSearchInput.value);
      selectEl.innerHTML = '<option value="">seleziona giocatore…</option>' + renderNominationPlayerOptionsMarkup(model.players);
      const stillVisible = previousSelectedValue !== "" && model.players.some(p => String(p.fantacalcioId) === previousSelectedValue);
      selectEl.value = stillVisible ? previousSelectedValue : "";
      const disabled = nominationPickerIsDisabled(model.state);
      selectEl.disabled = disabled;
      nominatePlayerSearchInput.disabled = disabled;
      if(nominateBtn) nominateBtn.disabled = disabled;
      pickerEl.setAttribute("data-nomination-player-state", model.state);
      const msgEl = pickerEl.querySelector("[data-nomination-player-message]");
      if(msgEl){
        msgEl.textContent = NOMINATION_PICKER_STATE_MESSAGES[model.state] || "";
        msgEl.hidden = model.state === "ready";
      }
    });
  }

  // Round alpha.7 (A7-P1-1 §12): anteprima statistiche del candidato selezionato — token dedicato
  // (_statsPreviewToken), MAI _renderGeneration (quest'ultimo è per il render strutturale intero,
  // troppo grossolano per una singola interazione locale sul <select>). Una risposta tardiva per un
  // candidato ormai non più selezionato può ancora popolare la cache (fetchPlayerStatsCached scrive
  // sempre) ma non deve MAI ridipingere il pannello ormai non più corrente (A7-P1-1D2 §12).
  const nominatePlayerSelect = container.querySelector("#nominatePlayerSelect");
  if(nominatePlayerSelect){
    nominatePlayerSelect.addEventListener("change", () => {
      const fantacalcioId = Number(nominatePlayerSelect.value) || null;
      const myPreviewToken = ++_statsPreviewToken;
      if(!fantacalcioId){
        repaintStatsPanel(container, "candidatePreviewStatsPanel", "idle", null);
        return;
      }
      repaintStatsPanel(container, "candidatePreviewStatsPanel", "loading", null);
      fetchPlayerStatsCached(ctx, catalogPlayers, fantacalcioId, _statsSeasonKey)
        .then(stats => {
          if(myPreviewToken !== _statsPreviewToken) return;   // anteprima superata — mai un repaint stale
          repaintStatsPanel(container, "candidatePreviewStatsPanel", stats ? "ready" : "empty", stats);
        })
        .catch(() => {
          if(myPreviewToken !== _statsPreviewToken) return;
          repaintStatsPanel(container, "candidatePreviewStatsPanel", "error", null);
        });
    });
  }

  const nominateForm = container.querySelector("#nominateForm");
  if(nominateForm){
    nominateForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const msg = container.querySelector("#nominateMsg");
      const seatId = container.querySelector("#nominateSeat").value;
      const fantacalcioId = Number(container.querySelector("#nominatePlayerSelect").value);
      if(!fantacalcioId){ if(msg) msg.innerHTML = '<p class="errtext">Seleziona un giocatore.</p>'; return; }
      // A7-P1-7 (§12 del design D2) — controllo di coerenza lato client, MAI una seconda autorità:
      // stesso identico modello puro usato dal render/dal filtro di ricerca sopra, mai un algoritmo
      // divergente. Impedisce SOLO che il picker sottometta un valore stale/non più visibile (una
      // select disabilitata forzata via DOM, o una corsa fra digitazione e submit) — il backend
      // nominate_player() resta l'unica autorità reale sullo stato "venduto" (§13/CORREZIONE
      // preservata: quando purchasesError è vero il modello usa il solo catalogo grezzo filtrato
      // dalla ricerca, MAI un rifiuto client-side basato su un'eleggibilità sconosciuta).
      const searchInputEl = container.querySelector("#nominatePlayerSearch");
      const currentModel = computeNominationCandidateModel(catalogPlayers, catalogPlayersError, purchases, purchasesError, searchInputEl ? searchInputEl.value : "");
      if(!currentModel.players.some(p => p.fantacalcioId === fantacalcioId)){
        if(msg) msg.innerHTML = '<p class="errtext">Il giocatore selezionato non è più disponibile. Aggiorna la ricerca e riprova.</p>';
        return;
      }
      const expectedRevision = liveSession ? liveSession.revision : 0;
      if(_activeLiveController) _activeLiveController.invalidateForMutation();   // §4/CORREZIONE B
      const result = await ctx.repository.nominatePlayer(auctionId, seatId, fantacalcioId, expectedRevision, genCommandId());
      if(!result.ok){
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Impossibile nominare il giocatore.") + "</p>" +
          (result.conflict ? '<p class="hint">Un\'altra scheda ha già modificato la sessione: ricarica la pagina.</p>' : "");
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  }

  const bidForm = container.querySelector("#bidForm");
  if(bidForm && liveSession){
    bidForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const msg = container.querySelector("#bidMsg");
      const mySeat = seats.find(s => s.assignedUserId === myUserId);
      if(!mySeat){ if(msg) msg.innerHTML = '<p class="errtext">Non controlli nessuna squadra in questa asta.</p>'; return; }
      const amount = Number(container.querySelector("#bidAmount").value);
      if(_activeLiveController) _activeLiveController.invalidateForMutation();   // §4/CORREZIONE B
      // Round alpha.6 P0.2-A1 (CORREZIONE Q): letti al momento del submit dall'ultima reference
      // server-authoritative applicata (currentLiveSnapshotRef), MAI dal `liveSession` catturato dal
      // cold-render — un rival bid applicato via patch non strutturale nel frattempo (mentre il form
      // resta intatto, §8 punto 3) avrebbe reso `liveSession.bidSeq` obsoleto.
      const result = await ctx.repository.placeBid(auctionId, currentLiveSnapshotRef.current.currentNominationId, mySeat.id, amount, currentLiveSnapshotRef.current.bidSeq, genCommandId());
      if(!result.ok){
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Offerta non riuscita.") + "</p>" +
          (result.conflict ? '<p class="hint">Un\'altra offerta è già stata registrata: ricarica la pagina.</p>' : "");
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId);
    });
  }

  // Countdown: SOLO display, ricalcolato ogni secondo dal deadlineAt server-authoritative già
  // ottenuto — mai un nuovo timer "inventato" lato client. Alla scadenza abilita il pulsante
  // "Finalizza" (mai una chiamata automatica: §UI, "auto-finalize non obbligatorio"). Round alpha.6
  // P0.2-A (CORREZIONE O+P, §8): unica primitive condivisa col patch non strutturale del poll —
  // sostituisce la precedente sequenza inline (const deadlineMs immutabile + tick() locale +
  // setInterval), mai due implementazioni divergenti.
  const countdownEl = container.querySelector("#nominationCountdown");
  const finalizeBtn = container.querySelector("#finalizeNominationBtn");
  if(countdownEl && liveSession && liveSession.deadlineAt){
    applyNominationCountdownDeadline(liveSession.deadlineAt, countdownEl, finalizeBtn);
  }
  if(finalizeBtn){
    finalizeBtn.addEventListener("click", async () => {
      finalizeBtn.disabled = true;
      // Round RC-B1 (design RC-B1D, accettato): pulisce SEMPRE un eventuale messaggio di fallimento
      // di un tentativo precedente all'inizio di uno nuovo — il pulsante è cliccabile solo se
      // attualmente montato, quindi questo container.querySelector() qui, PRIMA di qualunque await,
      // trova sempre l'elemento corrente, mai uno stale (a differenza delle scritture equivalenti più
      // sotto, dopo l'await, che devono invece passare dalla guardia di generazione).
      const msgAtStart = container.querySelector("#finalizeNominationMsg");
      if(msgAtStart) msgAtStart.innerHTML = "";
      if(_activeLiveController) _activeLiveController.invalidateForMutation();   // §4/CORREZIONE B
      // Round RC-B1: un rigetto realmente inatteso (mai visto nel contratto tracciato di
      // finalizeIfExpired(), che risolve sempre {ok,...} — difesa in profondità, stesso principio già
      // stabilito per il logout in Beta-P3-9) non deve mai sfuggire come promise rejection non
      // gestita da questo handler.
      let result;
      try{
        result = await ctx.repository.finalizeIfExpired(auctionId, liveSession.currentNominationId);
      }catch(e){
        if(myGeneration !== _renderGeneration) return;   // CORREZIONE R — nessuna scrittura se stale
        finalizeBtn.disabled = false;
        const msg = container.querySelector("#finalizeNominationMsg");
        if(msg) msg.innerHTML = '<p class="errtext">Finalizzazione non riuscita.</p>';
        return;
      }
      if(!result.ok){
        // Round RC-B1 (design RC-B1D, accettato): il rigetto "non ancora scaduta" (fra gli altri) di
        // finalize_if_expired() è testo applicativo già curato in italiano dalla RPC stessa — stesso
        // contratto già usato senza modifiche da result.error nel gestore adiacente "Finalizza
        // acquisto" (settleNominationBtn, poco sotto) — mai un parsing di pattern specifici, mai
        // un'interpretazione speciale: mostrato così com'è, con lo stesso fallback generico.
        if(myGeneration !== _renderGeneration) return;   // CORREZIONE R — nessuna scrittura se stale
        finalizeBtn.disabled = false;
        const msg = container.querySelector("#finalizeNominationMsg");
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Finalizzazione non riuscita.") + "</p>";
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      // CORREZIONE round alpha.5-r4-r2 (BLOCKER 5, audit indipendente): l'esito appena ottenuto viene
      // passato al render successivo SOLO per mostrare, una volta sola, il messaggio distinto
      // "Nomina conclusa: giocatore invenduto." quando outcome==="unsold" — mai persistito.
      renderAuctionDetailPage(container, ctx, auctionId, undefined, result.outcome);
    });
  }

  // Round alpha.5-r5-r1: "Finalizza acquisto" — owner o current member (mai ristretto alla sola
  // winning seat lato client: il pulsante è visibile a chiunque, la server authority di
  // settle_nomination() decide). Idempotente: un secondo click su un esito già settled (es. doppio
  // click prima del re-render) restituisce lo stesso esito storico, mai un errore.
  const settleBtn = container.querySelector("#settleNominationBtn");
  if(settleBtn && liveSession){
    settleBtn.addEventListener("click", async () => {
      settleBtn.disabled = true;
      const msg = container.querySelector("#settleNominationMsg");
      if(_activeLiveController) _activeLiveController.invalidateForMutation();   // §4/CORREZIONE B
      const result = await ctx.repository.settleNomination(auctionId, liveSession.currentNominationId);
      if(!result.ok){
        settleBtn.disabled = false;
        if(msg) msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Finalizzazione non riuscita.") + "</p>";
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      renderAuctionDetailPage(container, ctx, auctionId, undefined, undefined,
        { playerFantacalcioId: result.playerFantacalcioId, seatId: result.seatId, amount: result.amount });
    });
  }
}

function renderInitialSetupForm(){
  return (
    '<div class="card">' +
      "<h3>Configura la prima versione dell'asta</h3>" +
      '<p class="hint">Questa asta non ha ancora uno stato salvato: imposta i parametri iniziali per iniziare — potrai regolarli in seguito.</p>' +
      '<form id="initAuctionForm">' +
        '<label class="field">Partecipanti' +
          '<input type="number" id="initPartecipanti" min="2" max="20" step="1" required></label>' +
        '<label class="field">Budget crediti a squadra' +
          '<input type="number" id="initBudget" min="50" max="5000" step="1" required></label>' +
        '<label class="field">Ripartizione budget' +
          '<select id="initBudgetPreset">' +
            '<option value="equilibrato" selected>Equilibrato</option>' +
            '<option value="prudente">Prudente</option>' +
            '<option value="aggressivo">Aggressivo</option>' +
          '</select></label>' +
        '<button type="submit" class="btn primary">Crea configurazione iniziale</button>' +
      '</form>' +
      '<div id="initAuctionMsg"></div>' +
    '</div>'
  );
}

function wireInitialSetupForm(container, ctx, auctionId, currentRevision){
  // Round alpha.6 P0.2-A1 (CORREZIONE R) — vedi wireLobbySection() sopra per la motivazione completa.
  const myGeneration = _renderGeneration;
  const form = container.querySelector("#initAuctionForm");
  if(!form) return;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = container.querySelector("#initAuctionMsg");
    const submitBtn = form.querySelector('button[type="submit"]');
    const partecipanti = container.querySelector("#initPartecipanti").value;
    const budget = container.querySelector("#initBudget").value;
    const budgetPresetKey = container.querySelector("#initBudgetPreset").value;

    // Validazione lato client PRIMA di costruire lo stato: stesso messaggio d'errore che
    // createInitialAuctionState() userebbe comunque (funzione condivisa, mai duplicata) — qui solo
    // per mostrarlo senza dover intercettare un'eccezione per il caso comune di input non valido.
    const setupErrors = validateInitialAuctionSetup({ partecipanti, budget });
    if(setupErrors.length){ msg.innerHTML = '<p class="errtext">' + escapeHtml(setupErrors.join(" ")) + "</p>"; return; }

    submitBtn.disabled = true;
    msg.innerHTML = '<p class="hint">Salvataggio in corso…</p>';
    try{
      const initialState = createInitialAuctionState({ partecipanti, budget, budgetPresetKey });
      // expectedRevision = currentRevision (0, catturata al render di questa pagina): se nel
      // frattempo un'altra scheda ha già inizializzato questa stessa asta, il repository restituisce
      // conflict:true (concorrenza ottimistica esistente, invariata) — mai una doppia
      // inizializzazione, mai una sovrascrittura silenziosa.
      const result = await ctx.repository.saveAuction(auctionId, initialState, currentRevision);
      if(!result.ok){
        submitBtn.disabled = false;
        msg.innerHTML = '<p class="errtext">' + escapeHtml(result.error || "Salvataggio non riuscito.") + "</p>" +
          (result.conflict ? '<p class="hint">Un\'altra scheda ha già inizializzato questa asta: ricarica la pagina.</p>' : "");
        return;
      }
      if(myGeneration !== _renderGeneration) return;   // CORREZIONE R
      await renderAuctionDetailPage(container, ctx, auctionId);
    }catch(err){
      submitBtn.disabled = false;
      msg.innerHTML = '<p class="errtext">' + escapeHtml(err && err.message ? err.message : String(err)) + "</p>";
    }
  });
}
